import React, { useState, useEffect, useRef, useMemo } from 'react';
import { AuthUser, getStoredToken } from '../utils/authApi';
import {
  printReceiptUSB,
  isUSBPrinterConnected,
  connectUSBPrinter,
  tryAutoReconnectUSB,
  getUSBPrinterName,
} from '../utils/usbPrinter';
import {
  printReceiptBT,
  connectBluetoothPrinter,
  getBTPrinterName,
} from '../utils/bluetoothPrinter';
import {
  Plus, Trash2, Users, X, Minus, Search, ShoppingCart,
  Utensils, Move, Square, Circle as CircleIcon, Send, Receipt,
  RotateCw, RotateCcw, ZoomIn, ZoomOut, ChefHat, Pencil,
  Clock, History, Printer, CheckCircle2, Calendar, UserCheck,
  AlertTriangle, MessageSquare, Filter,
} from 'lucide-react';

const BACKEND = import.meta.env.VITE_API_URL || 'https://api.cloudairambo.com';
const LAYOUT_SAVE_DEBOUNCE_MS = 800;

async function apiGetLayout(outletId: number, jwt: string) {
  const res = await fetch(`${BACKEND}/table-layout?outlet_id=${outletId}`, {
    headers: jwt ? { Authorization: `Bearer ${jwt}` } : {},
  });
  if (!res.ok) throw new Error(`GET /table-layout failed: ${res.status}`);
  return res.json();
}

async function apiPutLayout(
  outletId: number,
  jwt: string,
  payload: { tables: FloorTable[]; floors: Floor[]; landmarks: FloorLandmark[] }
) {
  const res = await fetch(`${BACKEND}/table-layout?outlet_id=${outletId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}) },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error(`PUT /table-layout failed: ${res.status}`);
  return res.json();
}

const ORDERS_POLL_MS = 5000;

async function apiGetOrders(outletId: number, jwt: string) {
  const res = await fetch(`${BACKEND}/table-orders?outlet_id=${outletId}`, {
    headers: jwt ? { Authorization: `Bearer ${jwt}` } : {},
  });
  if (!res.ok) throw new Error(`GET /table-orders failed: ${res.status}`);
  return res.json();
}

async function apiPutOrders(
  outletId: number,
  jwt: string,
  orders: Record<string, TableOrderItem[]>,
  servedStatus: Record<string, boolean>
) {
  const res = await fetch(`${BACKEND}/table-orders?outlet_id=${outletId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}) },
    body: JSON.stringify({ orders, served_status: servedStatus }),
  });
  if (!res.ok) throw new Error(`PUT /table-orders failed: ${res.status}`);
  return res.json();
}

let tableAudioCtx: AudioContext | null = null;
function playReadyBell() {
  try {
    if (!tableAudioCtx) {
      tableAudioCtx = new (window.AudioContext || (window as any).webkitAudioContext)();
    }
    if (tableAudioCtx.state === 'suspended') tableAudioCtx.resume();
    const ctx = tableAudioCtx;
    [880, 1108].forEach((freq, i) => {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.connect(g); g.connect(ctx.destination);
      o.type = 'sine';
      o.frequency.value = freq;
      const t = ctx.currentTime + i * 0.22;
      g.gain.setValueAtTime(0, t);
      g.gain.linearRampToValueAtTime(0.5, t + 0.03);
      g.gain.exponentialRampToValueAtTime(0.001, t + 0.7);
      o.start(t); o.stop(t + 0.7);
    });
  } catch {}
}

// ─── Types ──────────────────────────────────────────────────────────────────

type TableShape = 'square' | 'round';
type TableStatus = 'empty' | 'occupied' | 'reserved';

interface Floor {
  id: string;
  name: string;
}

type LandmarkType = 'kitchen' | 'counter' | 'entrance' | 'restroom' | 'bar';

interface FloorLandmark {
  id: string;
  floorId: string;
  type: LandmarkType;
  label: string;
  x: number;
  y: number;
  rotation?: number;
}

const LANDMARK_META: Record<LandmarkType, { emoji: string; defaultLabel: string; color: string }> = {
  kitchen:  { emoji: '🍳', defaultLabel: 'Kitchen',  color: '#dc2626' },
  counter:  { emoji: '🧾', defaultLabel: 'Counter',  color: '#0f766e' },
  entrance: { emoji: '🚪', defaultLabel: 'Entrance', color: '#0284c7' },
  restroom: { emoji: '🚻', defaultLabel: 'Restroom', color: '#6366f1' },
  bar:      { emoji: '🍸', defaultLabel: 'Bar',      color: '#d97706' },
};

export type ReservationStatus = 'confirmed' | 'seated' | 'completed' | 'cancelled' | 'no-show';

export interface TableReservation {
  id: string;
  tableId: string;
  name: string;
  phone: string;
  partySize: number;
  date: string;          // "YYYY-MM-DD"
  time: string;          // "HH:MM" 24h
  durationMinutes: number; // default 90m
  specialRequests?: string;
  status: ReservationStatus;
  notedAt: number;
}

function todayLocalISODate(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function deviceUses24Hour(): boolean {
  const sample = new Date(2020, 0, 1, 13, 0, 0).toLocaleTimeString();
  return !/am|pm/i.test(sample);
}

function currentTimeRounded5(d: Date = new Date()): string {
  let h = d.getHours();
  let m = Math.round(d.getMinutes() / 5) * 5;
  if (m === 60) { m = 0; h = (h + 1) % 24; }
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

// Convert "HH:MM" to minutes from midnight
function timeToMins(t: string): number {
  if (!t) return 0;
  const [h, m] = t.split(':').map(Number);
  return (h || 0) * 60 + (m || 0);
}

// Check if two time ranges overlap
function doReservationsOverlap(
  resA: { date: string; time: string; durationMinutes: number },
  resB: { date: string; time: string; durationMinutes: number }
): boolean {
  if (resA.date !== resB.date) return false;
  const startA = timeToMins(resA.time);
  const endA = startA + (resA.durationMinutes || 90);
  const startB = timeToMins(resB.time);
  const endB = startB + (resB.durationMinutes || 90);
  return Math.max(startA, startB) < Math.min(endA, endB);
}

// Get all reservations across tables normalized
function getNormalizedTableReservations(t: FloorTable): TableReservation[] {
  const list: TableReservation[] = Array.isArray(t.reservations) ? [...t.reservations] : [];
  if (t.reservation && !list.some(r => r.id === (t.reservation as any).id || (r.date === t.reservation?.date && r.time === t.reservation?.time))) {
    list.push({
      id: `res-legacy-${t.id}`,
      tableId: t.id,
      name: t.reservation.name,
      phone: t.reservation.phone,
      partySize: t.reservation.partySize || 2,
      date: t.reservation.date || todayLocalISODate(),
      time: t.reservation.time || '19:00',
      durationMinutes: 90,
      status: 'confirmed',
      notedAt: t.reservation.notedAt || Date.now(),
    });
  }
  return list.filter(r => r.status !== 'cancelled');
}

// Get the active (or upcoming within 45m) reservation for a table right now
function getTableActiveOrNextReservation(t: FloorTable, now: Date): { active?: TableReservation; next?: TableReservation } {
  const list = getNormalizedTableReservations(t);
  const today = todayLocalISODate();
  const currentMins = now.getHours() * 60 + now.getMinutes();

  let active: TableReservation | undefined;
  let next: TableReservation | undefined;
  let nextDiff = Infinity;

  for (const r of list) {
    if (r.date !== today || r.status === 'completed' || r.status === 'cancelled' || r.status === 'no-show') continue;
    const rStart = timeToMins(r.time);
    const rEnd = rStart + (r.durationMinutes || 90);

    // Active: current time is between 15 mins before booking and end of booking
    if (currentMins >= rStart - 15 && currentMins < rEnd && r.status !== 'seated') {
      active = r;
      break;
    }

    // Next upcoming today
    if (rStart > currentMins && (rStart - currentMins) < nextDiff) {
      nextDiff = rStart - currentMins;
      next = r;
    }
  }

  return { active, next };
}

interface FloorTable {
  id: string;
  number: string;
  label?: string;
  floorId: string;
  shape: TableShape;
  seats: number;
  x: number;
  y: number;
  width: number;
  height: number;
  rotation?: number;
  reservation?: TableReservation; // backward compatibility
  reservations?: TableReservation[]; // multiple bookings per table
  attendedBy?: string;
}

export type CourseType = 'beverage' | 'starter' | 'main' | 'dessert';

export interface TableOrderItem {
  id: string;
  productId?: number;
  name: string;
  rate: number;
  qty: number;
  kotRound: number;
  sentToKitchen: boolean;
  notes?: string;
  course?: CourseType;
}

export interface KOTHistoryEntry {
  id: string;
  kotNumber: number;
  round: number;
  tableId: string;
  tableNumber: string;
  floorName: string;
  serverName: string;
  timestamp: number;
  items: { name: string; qty: number; notes?: string; course?: CourseType }[];
  isVoid?: boolean;
  voidReason?: string;
}

interface Product {
  id: number;
  name: string;
  barcode: string;
  rate: number;
  unit?: string;
  category?: string;
  stock_quantity?: number;
}

export interface TableCheckoutPayload {
  tableId: string;
  tableNumber: string;
  items: TableOrderItem[];
}

interface TableLayoutViewProps {
  authUser: AuthUser | null;
  outletId?: number | null;
  activeCurrency?: string;
  sym?: string;
  onCheckout?: (payload: TableCheckoutPayload) => void;
  canAccessKitchenDashboard?: boolean;
  canEditTableLayout?: boolean;
  canManageFloors?: boolean;
}

const STORAGE_KEY = 'cloudairambo_table_layout';
const ORDERS_KEY_PREFIX = 'cloudairambo_table_orders_';
const FLOORS_KEY = 'cloudairambo_table_floors';
const LANDMARKS_KEY = 'cloudairambo_table_landmarks';
const KOT_AUTO_PRINT_KEY = 'cloudairambo_kot_auto_print';
const KOT_TOKENS_KEY = 'cloudairambo_kot_tokens';
const SERVED_STATUS_KEY = 'cloudairambo_table_served_status';
const KOT_HISTORY_KEY = 'cloudairambo_kot_history_v1';
const KOT_SEQ_KEY = 'cloudairambo_kot_seq_counter';

const COURSE_META: Record<CourseType, { label: string; emoji: string; color: string }> = {
  starter:  { label: 'Starter',  emoji: '🥗', color: '#16a34a' },
  main:     { label: 'Main',     emoji: '🍛', color: '#d97706' },
  dessert:  { label: 'Dessert',  emoji: '🍰', color: '#db2777' },
  beverage: { label: 'Beverage', emoji: '🥤', color: '#0284c7' },
};

const COMMON_NOTE_TAGS = ['Less Spicy', 'Extra Spicy', 'No Onion / Garlic', 'Jain Prep', 'Parcel / Pack'];
const VOID_REASONS = ['Guest changed mind', 'Punched wrong item', 'Kitchen delayed', 'Item unavailable / 86'];
const DEFAULT_FLOOR: Floor = { id: 'floor-1', name: 'Floor 1' };

const loadLayout = (): FloorTable[] => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.map((t: FloorTable) => ({ ...t, floorId: t.floorId || DEFAULT_FLOOR.id })) : [];
  } catch { return []; }
};
const saveLayout = (tables: FloorTable[]) => localStorage.setItem(STORAGE_KEY, JSON.stringify(tables));

const loadOrders = (): Record<string, TableOrderItem[]> => {
  try {
    const raw = localStorage.getItem(ORDERS_KEY_PREFIX + 'v1');
    return raw ? JSON.parse(raw) : {};
  } catch { return {}; }
};
const saveOrders = (orders: Record<string, TableOrderItem[]>) => localStorage.setItem(ORDERS_KEY_PREFIX + 'v1', JSON.stringify(orders));

const loadFloors = (): Floor[] => {
  try {
    const raw = localStorage.getItem(FLOORS_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) && parsed.length > 0 ? parsed : [DEFAULT_FLOOR];
  } catch { return [DEFAULT_FLOOR]; }
};
const saveFloors = (floors: Floor[]) => localStorage.setItem(FLOORS_KEY, JSON.stringify(floors));

const loadLandmarks = (): FloorLandmark[] => {
  try {
    const raw = localStorage.getItem(LANDMARKS_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
};
const saveLandmarks = (landmarks: FloorLandmark[]) => localStorage.setItem(LANDMARKS_KEY, JSON.stringify(landmarks));

// ─── Shared Styles ──────────────────────────────────────────────────────────

const btn: React.CSSProperties = {
  padding: '6px 12px', borderRadius: 8, border: '1px solid #e2e8f0',
  background: '#fff', color: '#334155', fontSize: 12, fontWeight: 700,
  cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
  transition: 'all 0.12s ease',
};

const readyPulseStyle = `
  @keyframes tableReadyPulse {
    0%, 100% { transform: scale(1); }
    50% { transform: scale(1.06); }
  }
`;

export default function TableLayoutView({
  authUser, outletId = null, activeCurrency = 'USD', sym = '$',
  onCheckout,
  canAccessKitchenDashboard = true, canEditTableLayout = true, canManageFloors = true,
}: TableLayoutViewProps) {
  const [tables, setTables] = useState<FloorTable[]>(() => loadLayout());
  const [orders, setOrders] = useState<Record<string, TableOrderItem[]>>(() => loadOrders());
  const [kotRounds, setKotRounds] = useState<Record<string, number>>({});
  const [mode, setMode] = useState<'design' | 'order'>('order');
  const [selectedTableId, setSelectedTableId] = useState<string | null>(null);
  const [orderTableId, setOrderTableId] = useState<string | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [productSearch, setProductSearch] = useState('');
  const [outletKitchenToken, setOutletKitchenToken] = useState('');
  const [tableKitchenStatus, setTableKitchenStatus] = useState<Record<string, 'waiting' | 'ready'>>({});
  const [tableServedStatus, setTableServedStatus] = useState<Record<string, boolean>>(() => {
    try {
      const raw = localStorage.getItem(SERVED_STATUS_KEY);
      return raw ? JSON.parse(raw) : {};
    } catch { return {}; }
  });
  const [tableTimings, setTableTimings] = useState<Record<string, { firstKotAt?: number; readyAt?: number }>>(() => {
    try {
      const raw = localStorage.getItem('cloudairambo_table_timings');
      return raw ? JSON.parse(raw) : {};
    } catch { return {}; }
  });
  useEffect(() => { localStorage.setItem('cloudairambo_table_timings', JSON.stringify(tableTimings)); }, [tableTimings]);

  const [vacantConfirm, setVacantConfirm] = useState<{ tableId: string } | null>(null);
  const [kotPrintConfirm, setKotPrintConfirm] = useState(false);
  // ── Master Reservation Book States ─────────────────────────────────────────
  const [showAllReservationsModal, setShowAllReservationsModal] = useState(false);
  const [resViewDate, setResViewDate] = useState(() => todayLocalISODate());
  const [resStatusFilter, setResStatusFilter] = useState<'all' | 'confirmed' | 'seated'>('all');

  // Single Reservation Edit / Add Modal
  const [reservationModal, setReservationModal] = useState<{ tableId: string; resId?: string } | null>(null);
  const [resName, setResName] = useState('');
  const [resPhone, setResPhone] = useState('');
  const [resParty, setResParty] = useState(2);
  const [resTime, setResTime] = useState('');
  const [resDate, setResDate] = useState(() => todayLocalISODate());
  const [resDuration, setResDuration] = useState(90);
  const [resNotes, setResNotes] = useState('');
  const [conflictWarning, setConflictWarning] = useState<string | null>(null);

  const use24Hour = useMemo(() => deviceUses24Hour(), []);
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const interval = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(interval);
  }, []);

  const openReserveModal = (tableId: string, resId?: string) => {
    const t = tables.find(tt => tt.id === tableId);
    if (!t) return;
    const existingList = getNormalizedTableReservations(t);
    const existing = resId ? existingList.find(r => r.id === resId) : undefined;

    setResName(existing?.name || '');
    setResPhone(existing?.phone || '');
    setResParty(existing?.partySize || t.seats || 2);
    setResTime(existing?.time || currentTimeRounded5(now));
    setResDate(existing?.date || todayLocalISODate());
    setResDuration(existing?.durationMinutes || 90);
    setResNotes(existing?.specialRequests || '');
    setConflictWarning(null);
    setReservationModal({ tableId, resId });
  };

  // Re-check conflict whenever date, time, duration or table changes
  useEffect(() => {
    if (!reservationModal) return;
    const t = tables.find(tt => tt.id === reservationModal.tableId);
    if (!t || !resTime || !resDate) { setConflictWarning(null); return; }

    const candidate = { date: resDate, time: resTime, durationMinutes: resDuration };
    const allTableBookings = getNormalizedTableReservations(t).filter(
      r => r.id !== reservationModal.resId && r.status !== 'cancelled'
    );

    const overlap = allTableBookings.find(r => doReservationsOverlap(candidate, r));
    if (overlap) {
      setConflictWarning(`⚠️ Time Conflict: Table ${t.number} already reserved for "${overlap.name}" at ${overlap.time} (${overlap.durationMinutes}m)`);
    } else {
      setConflictWarning(null);
    }
  }, [resDate, resTime, resDuration, reservationModal, tables]);

  const saveReservation = () => {
    if (!reservationModal) return;
    if (!resName.trim() || !resTime || !resDate) {
      alert('Guest name, date, and start time are required.');
      return;
    }

    const t = tables.find(tt => tt.id === reservationModal.tableId);
    if (!t) return;

    const currentList = getNormalizedTableReservations(t);
    const isEdit = Boolean(reservationModal.resId);

    const newRes: TableReservation = {
      id: reservationModal.resId || `res-${Date.now()}-${Math.random().toString(36).substr(2, 4)}`,
      tableId: t.id,
      name: resName.trim(),
      phone: resPhone.trim(),
      partySize: Number(resParty) || 2,
      date: resDate,
      time: resTime,
      durationMinutes: Number(resDuration) || 90,
      specialRequests: resNotes.trim(),
      status: 'confirmed',
      notedAt: Date.now(),
    };

    let updatedList: TableReservation[];
    if (isEdit) {
      updatedList = currentList.map(r => r.id === reservationModal.resId ? newRes : r);
    } else {
      updatedList = [...currentList, newRes];
    }

    updateTable(t.id, {
      reservations: updatedList,
      reservation: updatedList[0], // fallback mirror
    });
    setReservationModal(null);
  };

  const updateReservationStatus = (tableId: string, resId: string, status: ReservationStatus) => {
    const t = tables.find(tt => tt.id === tableId);
    if (!t) return;
    const currentList = getNormalizedTableReservations(t);
    const updated = currentList.map(r => r.id === resId ? { ...r, status } : r);
    updateTable(tableId, {
      reservations: updated,
      reservation: updated.find(r => r.status === 'confirmed'),
    });
  };

  const seatReservation = (tableId: string, res: TableReservation) => {
    updateReservationStatus(tableId, res.id, 'seated');
    updateTable(tableId, { attendedBy: res.name });
    setOrderTableId(tableId);
    setShowAllReservationsModal(false);
  };

  const [floors, setFloors] = useState<Floor[]>(() => loadFloors());
  const [activeFloorId, setActiveFloorId] = useState<string>(() => loadFloors()[0]?.id || DEFAULT_FLOOR.id);
  const [editingFloorId, setEditingFloorId] = useState<string | null>(null);
  const [landmarks, setLandmarks] = useState<FloorLandmark[]>(() => loadLandmarks());
  const [selectedLandmarkId, setSelectedLandmarkId] = useState<string | null>(null);
  const [showOrderPopup, setShowOrderPopup] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  
  const [editingItemNote, setEditingItemNote] = useState<{ itemId: string; name: string; notes: string; course?: CourseType } | null>(null);
  const [voidPrompt, setVoidPrompt] = useState<{ itemId: string; name: string; qty: number; round: number } | null>(null);
  const [voidReason, setVoidReason] = useState(VOID_REASONS[0]);

  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [kotHistory, setKotHistory] = useState<KOTHistoryEntry[]>(() => {
    try {
      const raw = localStorage.getItem(KOT_HISTORY_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  });
  useEffect(() => {
    localStorage.setItem(KOT_HISTORY_KEY, JSON.stringify(kotHistory.slice(-100)));
  }, [kotHistory]);

  const [autoPrintKOT, setAutoPrintKOT] = useState(() => localStorage.getItem(KOT_AUTO_PRINT_KEY) !== 'false');
  const [lastKOT, setLastKOT] = useState<{ tableId: string; round: number; items: TableOrderItem[] } | null>(null);
  const [showPrintSettings, setShowPrintSettings] = useState(false);
  const [useUSBPrinter, setUseUSBPrinter] = useState(() => localStorage.getItem('cloudairambo_kot_usb_print') === 'true');
  const [usbConnected, setUsbConnected] = useState(false);
  const [useBTPrinter, setUseBTPrinter] = useState(() => localStorage.getItem('cloudairambo_kot_bt_print') === 'true');
  const [btConnected, setBtConnected] = useState(() => !!getBTPrinterName());

  useEffect(() => {
    tryAutoReconnectUSB().then(ok => setUsbConnected(ok || isUSBPrinterConnected()));
  }, []);

  const [kitchenTokens, setKitchenTokens] = useState<Record<string, string>>(() => {
    try {
      const raw = localStorage.getItem(KOT_TOKENS_KEY);
      return raw ? JSON.parse(raw) : {};
    } catch { return {}; }
  });

  const canvasRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{ id: string; startX: number; startY: number; origX: number; origY: number } | null>(null);
  const resizeRef = useRef<{ id: string; startX: number; startY: number; origW: number; origH: number } | null>(null);
  const landmarkDragRef = useRef<{ id: string; startX: number; startY: number; origX: number; origY: number } | null>(null);
 const [drawerWidth, setDrawerWidth] = useState(680);
  const [isDraggingDrawer, setIsDraggingDrawer] = useState(false);
  const drawerResizeRef = useRef<{ startX: number; origW: number } | null>(null);
  const prevTableStatusRef = useRef<Record<string, 'waiting' | 'ready'> | null>(null);
  const ordersRef = useRef(orders);
  useEffect(() => { ordersRef.current = orders; }, [orders]);

  useEffect(() => { saveLayout(tables); }, [tables]);
  useEffect(() => { saveOrders(orders); }, [orders]);
  useEffect(() => { saveFloors(floors); }, [floors]);
  useEffect(() => { saveLandmarks(landmarks); }, [landmarks]);
  useEffect(() => { localStorage.setItem(KOT_TOKENS_KEY, JSON.stringify(kitchenTokens)); }, [kitchenTokens]);
  useEffect(() => { localStorage.setItem(SERVED_STATUS_KEY, JSON.stringify(tableServedStatus)); }, [tableServedStatus]);

  const skipNextLayoutSyncRef = useRef(false);
  const layoutLoadedRef = useRef(false);
  const pendingLayoutSaveRef = useRef(false);

  useEffect(() => {
    if (outletId == null) return;
    let cancelled = false;
    const poll = async () => {
      try {
        const jwt = getStoredToken() || '';
        const data = await apiGetLayout(outletId, jwt);
        if (cancelled || pendingLayoutSaveRef.current) return;
        skipNextLayoutSyncRef.current = true;
        if (Array.isArray(data.tables)) setTables(data.tables);
        if (Array.isArray(data.floors) && data.floors.length > 0) setFloors(data.floors);
        if (Array.isArray(data.landmarks)) setLandmarks(data.landmarks);
        layoutLoadedRef.current = true;
      } catch {
        layoutLoadedRef.current = true;
      }
    };
    poll();
    const interval = setInterval(poll, ORDERS_POLL_MS);
    return () => { cancelled = true; clearInterval(interval); };
  }, [outletId]);

  useEffect(() => {
    if (outletId == null || !layoutLoadedRef.current) return;
    if (skipNextLayoutSyncRef.current) { skipNextLayoutSyncRef.current = false; return; }
    pendingLayoutSaveRef.current = true;
    const jwt = getStoredToken() || '';
    const timer = setTimeout(() => {
      apiPutLayout(outletId, jwt, { tables, floors, landmarks })
        .finally(() => { pendingLayoutSaveRef.current = false; });
    }, LAYOUT_SAVE_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [tables, floors, landmarks, outletId]);

  const skipNextOrdersSyncRef = useRef(false);
  const ordersLoadedRef = useRef(false);

  useEffect(() => {
    if (outletId == null) return;
    let cancelled = false;
    const poll = async () => {
      try {
        const jwt = getStoredToken() || '';
        const data = await apiGetOrders(outletId, jwt);
        if (cancelled) return;
        skipNextOrdersSyncRef.current = true;
        setOrders(data.orders && typeof data.orders === 'object' ? data.orders : {});
        if (data.served_status && typeof data.served_status === 'object') {
          setTableServedStatus(data.served_status);
        }
        ordersLoadedRef.current = true;
      } catch {
        ordersLoadedRef.current = true;
      }
    };
    poll();
    const interval = setInterval(poll, ORDERS_POLL_MS);
    return () => { cancelled = true; clearInterval(interval); };
  }, [outletId]);

  useEffect(() => {
    if (outletId == null || !ordersLoadedRef.current) return;
    if (skipNextOrdersSyncRef.current) { skipNextOrdersSyncRef.current = false; return; }
    const jwt = getStoredToken() || '';
    const timer = setTimeout(() => {
      apiPutOrders(outletId, jwt, orders, tableServedStatus).catch(() => {});
    }, LAYOUT_SAVE_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [orders, tableServedStatus, outletId]);

  useEffect(() => {
    if (outletId == null) return;
    const jwt = localStorage.getItem('cloudairambo_jwt') || '';
    fetch(`${BACKEND}/products/?outlet_id=${outletId}`, { headers: jwt ? { Authorization: `Bearer ${jwt}` } : {} })
      .then(r => r.json())
      .then(data => setProducts(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, [outletId]);

  useEffect(() => {
    if (outletId == null) return;
    const jwt = localStorage.getItem('cloudairambo_jwt') || '';
    fetch(`${BACKEND}/outlets?module=pos`, { headers: jwt ? { Authorization: `Bearer ${jwt}` } : {} })
      .then(r => r.json())
      .then((outlets: any[]) => {
        const outlet = Array.isArray(outlets) ? outlets.find(o => o.id === outletId) : null;
        setOutletKitchenToken(outlet?.kitchen_token || '');
      })
      .catch(() => {});
  }, [outletId]);

  useEffect(() => {
    if (!outletKitchenToken) return;
    const poll = async () => {
      try {
        const jwt = localStorage.getItem('cloudairambo_jwt') || '';
        const res = await fetch(`${BACKEND}/tokens?outlet_token=${outletKitchenToken}`, { headers: jwt ? { Authorization: `Bearer ${jwt}` } : {} });
        const data = await res.json();
        if (!Array.isArray(data)) return;
        const next: Record<string, 'waiting' | 'ready'> = {};
        tables.forEach(t => {
          if (!ordersRef.current[t.id] || ordersRef.current[t.id].length === 0) return;
          const forTable = data.filter((o: any) => o.customer_name === `Table ${t.number}` && o.status !== 'collected');
          if (forTable.length === 0) return;
          next[t.id] = forTable.every((o: any) => o.status === 'ready') ? 'ready' : 'waiting';
        });

        const prevStatus = prevTableStatusRef.current;
        if (prevStatus) {
          const newlyReadyIds = Object.keys(next).filter(
            id => next[id] === 'ready' && prevStatus[id] && prevStatus[id] !== 'ready'
          );
          if (newlyReadyIds.length > 0) {
            playReadyBell();
            setTableTimings(prev => {
              const n = { ...prev };
              newlyReadyIds.forEach(id => { n[id] = { ...n[id], readyAt: Date.now() }; });
              return n;
            });
          }
        }
        prevTableStatusRef.current = next;
        setTableKitchenStatus(next);

        setTableServedStatus(prevServed => {
          const nextServed = { ...prevServed };
          let changed = false;
          Object.keys(next).forEach(id => {
            if (next[id] === 'waiting' && nextServed[id]) { delete nextServed[id]; changed = true; }
          });
          return changed ? nextServed : prevServed;
        });
      } catch {}
    };
    poll();
    const interval = setInterval(poll, 5000);
    return () => clearInterval(interval);
  }, [outletKitchenToken, tables]);

  const tableStatus = (id: string): TableStatus => {
    const items = orders[id] || [];
    if (items.length > 0) return 'occupied';
    const t = tables.find(tt => tt.id === id);
    if (!t) return 'empty';
    const { active } = getTableActiveOrNextReservation(t, now);
    return active ? 'reserved' : 'empty';
  };

  const addTable = (shape: TableShape) => {
    const floorTables = tables.filter(t => t.floorId === activeFloorId);
    const nextNum = String(floorTables.length + 1).padStart(2, '0');
    const t: FloorTable = {
      id: `tbl-${Date.now()}`,
      number: nextNum,
      floorId: activeFloorId,
      shape,
      seats: 4,
      x: 40, y: 40,
      width: shape === 'round' ? 90 : 100,
      height: shape === 'round' ? 90 : 70,
      rotation: 0,
    };
    setTables(prev => [...prev, t]);
  };

  const updateTable = (id: string, patch: Partial<FloorTable>) => {
    setTables(prev => prev.map(t => t.id === id ? { ...t, ...patch } : t));
  };

  const deleteTable = (id: string) => {
    if (!window.confirm('Remove this table from the floor plan?')) return;
    setTables(prev => prev.filter(t => t.id !== id));
    setOrders(prev => { const n = { ...prev }; delete n[id]; return n; });
    if (selectedTableId === id) setSelectedTableId(null);
  };

  const addFloor = () => {
    const usedNums = floors
      .map(f => parseInt(f.name.replace(/^Floor\s*/i, ''), 10))
      .filter(n => !isNaN(n));
    const nextNum = usedNums.length ? Math.max(...usedNums) + 1 : 1;
    const f: Floor = { id: `floor-${Date.now()}`, name: `Floor ${nextNum}` };
    setFloors(prev => [...prev, f]);
    setActiveFloorId(f.id);
  };

  const renameFloor = (id: string, name: string) => {
    setFloors(prev => prev.map(f => f.id === id ? { ...f, name } : f));
  };

  const deleteFloor = (id: string) => {
    if (floors.length <= 1) { alert('At least one floor is required.'); return; }
    if (!window.confirm('Remove this floor? Its tables and landmarks will also be removed.')) return;
    setTables(prev => prev.filter(t => t.floorId !== id));
    setLandmarks(prev => prev.filter(l => l.floorId !== id));
    setFloors(prev => {
      const remaining = prev.filter(f => f.id !== id);
      let n = 1;
      const renumbered = remaining.map(f =>
        /^Floor \d+$/i.test(f.name) ? { ...f, name: `Floor ${n++}` } : f
      );
      if (activeFloorId === id) setActiveFloorId(renumbered[0]?.id || DEFAULT_FLOOR.id);
      return renumbered;
    });
  };

  const addLandmark = (type: LandmarkType) => {
    const l: FloorLandmark = {
      id: `lm-${Date.now()}`, floorId: activeFloorId, type,
      label: LANDMARK_META[type].defaultLabel, x: 60, y: 60, rotation: 0,
    };
    setLandmarks(prev => [...prev, l]);
  };
  const updateLandmark = (id: string, patch: Partial<FloorLandmark>) => {
    setLandmarks(prev => prev.map(l => l.id === id ? { ...l, ...patch } : l));
  };
  const deleteLandmark = (id: string) => {
    setLandmarks(prev => prev.filter(l => l.id !== id));
    if (selectedLandmarkId === id) setSelectedLandmarkId(null);
  };

  const onTableMouseDown = (e: React.MouseEvent, t: FloorTable) => {
    if (mode !== 'design') return;
    e.stopPropagation();
    dragRef.current = { id: t.id, startX: e.clientX, startY: e.clientY, origX: t.x, origY: t.y };
  };

  const onResizeMouseDown = (e: React.MouseEvent, t: FloorTable) => {
    e.stopPropagation();
    resizeRef.current = { id: t.id, startX: e.clientX, startY: e.clientY, origW: t.width, origH: t.height };
  };

  const onLandmarkMouseDown = (e: React.MouseEvent, l: FloorLandmark) => {
    if (mode !== 'design') return;
    e.stopPropagation();
    landmarkDragRef.current = { id: l.id, startX: e.clientX, startY: e.clientY, origX: l.x, origY: l.y };
  };

  useEffect(() => {
    const DRAG_THRESHOLD = 4;
    const onMove = (e: MouseEvent) => {
      if (drawerResizeRef.current) {
        const { startX, origW } = drawerResizeRef.current;
        const dx = startX - e.clientX;
        setDrawerWidth(Math.max(340, Math.min(680, origW + dx)));
        return;
      }
      if (dragRef.current) {
        const { id, startX, startY, origX, origY } = dragRef.current;
        const dx = e.clientX - startX, dy = e.clientY - startY;
        if (Math.hypot(dx, dy) < DRAG_THRESHOLD) return;
        const canvasW = canvasRef.current?.clientWidth || 2000;
        const canvasH = canvasRef.current?.clientHeight || 2000;
        const t = tables.find(tt => tt.id === id);
        const maxX = canvasW - (t?.width || 80);
        const maxY = canvasH - (t?.height || 80);
        updateTable(id, {
          x: Math.max(0, Math.min(maxX, origX + dx)),
          y: Math.max(0, Math.min(maxY, origY + dy)),
        });
      } else if (resizeRef.current) {
        const { id, startX, startY, origW, origH } = resizeRef.current;
        const dx = e.clientX - startX, dy = e.clientY - startY;
        if (Math.hypot(dx, dy) < DRAG_THRESHOLD) return;
        updateTable(id, {
          width: Math.max(50, origW + dx),
          height: Math.max(50, origH + dy),
        });
      } else if (landmarkDragRef.current) {
        const { id, startX, startY, origX, origY } = landmarkDragRef.current;
        const dx = e.clientX - startX, dy = e.clientY - startY;
        if (Math.hypot(dx, dy) < DRAG_THRESHOLD) return;
        const canvasW = canvasRef.current?.clientWidth || 2000;
        const canvasH = canvasRef.current?.clientHeight || 2000;
        updateLandmark(id, {
          x: Math.max(0, Math.min(canvasW - 40, origX + dx)),
          y: Math.max(0, Math.min(canvasH - 30, origY + dy)),
        });
      }
    };
    const onUp = () => {
      dragRef.current = null; resizeRef.current = null; landmarkDragRef.current = null;
      if (drawerResizeRef.current) { drawerResizeRef.current = null; setIsDraggingDrawer(false); document.body.style.userSelect = ''; }
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [tables, landmarks]);

  const handleTableClick = (t: FloorTable) => {
    if (mode === 'design') {
      setSelectedTableId(t.id);
      setSelectedLandmarkId(null);
      return;
    }
    setOrderTableId(t.id);
  };

  const orderTable = tables.find(t => t.id === orderTableId) || null;
  const orderItems = orderTableId ? (orders[orderTableId] || []) : [];

  const addItemToOrder = (p: Product) => {
    if (!orderTableId) return;
    setOrders(prev => {
      const items = prev[orderTableId] || [];
      const existing = items.find(i => i.productId === p.id && !i.sentToKitchen);
      let next;
      if (existing) {
        next = items.map(i => i.id === existing.id ? { ...i, qty: i.qty + 1 } : i);
      } else {
        const newItem: TableOrderItem = {
          id: `oi-${Date.now()}-${Math.random()}`,
          productId: p.id, name: p.name, rate: p.rate, qty: 1,
          kotRound: (kotRounds[orderTableId] || 0) + 1,
          sentToKitchen: false,
        };
        next = [...items, newItem];
      }
      return { ...prev, [orderTableId]: next };
    });
  };

  const updateItemQty = (itemId: string, delta: number) => {
    if (!orderTableId) return;
    const current = (orders[orderTableId] || []).find(i => i.id === itemId);
    if (!current) return;

    if (current.sentToKitchen && delta < 0) {
      setVoidPrompt({ itemId: current.id, name: current.name, qty: 1, round: current.kotRound });
      return;
    }

    setOrders(prev => ({
      ...prev,
      [orderTableId]: (prev[orderTableId] || [])
        .map(i => i.id === itemId ? { ...i, qty: Math.max(0, i.qty + delta) } : i)
        .filter(i => i.qty > 0),
    }));
  };

  const removeItem = (itemId: string) => {
    if (!orderTableId) return;
    const current = (orders[orderTableId] || []).find(i => i.id === itemId);
    if (!current) return;

    if (current.sentToKitchen) {
      setVoidPrompt({ itemId: current.id, name: current.name, qty: current.qty, round: current.kotRound });
      return;
    }

    setOrders(prev => ({
      ...prev,
      [orderTableId]: (prev[orderTableId] || []).filter(i => i.id !== itemId),
    }));
  };

  const unsentItems = orderItems.filter(i => !i.sentToKitchen);
  const total = orderItems.reduce((s, i) => s + i.qty * i.rate, 0);

  interface PrintKOTOptions {
    round: number;
    items: { name: string; qty: number; notes?: string; course?: CourseType }[];
    table: FloorTable | null | undefined;
    kotNumber?: number;
    ticketType?: 'NEW' | 'RUNNING' | 'VOID' | 'REPRINT';
    voidReason?: string;
  }

  const printKOTTicket = async ({
    round, items, table,
    kotNumber,
    ticketType = (round > 1 ? 'RUNNING' : 'NEW'),
    voidReason
  }: PrintKOTOptions) => {
    const floorObj = floors.find(f => f.id === table?.floorId);
    const floorName = floorObj?.name || 'Main Floor';
    const serverName = table?.attendedBy || authUser?.name || 'Staff';
    const timeStr = new Date().toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit' });
    const dateStr = todayLocalISODate();
    const displayNum = kotNumber || round;

    if (useBTPrinter) {
      const btPrinted = await printReceiptBT({
        shopName: ticketType === 'VOID' ? '*** CANCELLED KOT ***' : (ticketType === 'REPRINT' ? '*** DUPLICATE KOT ***' : 'KITCHEN ORDER TICKET'),
        invoiceNumber: `KOT #${displayNum} (R${round})`,
        date: `${dateStr} ${timeStr}`,
        clientName: `T-${table?.number || ''} [${floorName}] | PAX: ${table?.seats || 2} | Svr: ${serverName}`,
        items: items.map(i => ({
          description: `${i.course ? `[${i.course.toUpperCase()}] ` : ''}${i.name}${i.notes ? `\n >> ${i.notes}` : ''}`,
          qty: i.qty,
          rate: 0
        })),
        taxLines: [],
        discountLines: [],
        shippingCost: 0,
        sym: '',
        notes: voidReason ? `REASON: ${voidReason}` : '',
        arabicNumerals: false,
      });
      if (btPrinted) return;
    }

    if (useUSBPrinter) {
      const usbPrinted = await printReceiptUSB({
        shopName: ticketType === 'VOID' ? '*** CANCELLED KOT ***' : (ticketType === 'REPRINT' ? '*** DUPLICATE KOT ***' : 'KITCHEN ORDER TICKET'),
        invoiceNumber: `KOT #${displayNum} (R${round})`,
        date: `${dateStr} ${timeStr}`,
        clientName: `T-${table?.number || ''} [${floorName}] | PAX: ${table?.seats || 2} | Svr: ${serverName}`,
        items: items.map(i => ({
          description: `${i.course ? `[${i.course.toUpperCase()}] ` : ''}${i.name}${i.notes ? `\n >> ${i.notes}` : ''}`,
          qty: i.qty,
          rate: 0
        })),
        taxLines: [],
        discountLines: [],
        shippingCost: 0,
        sym: '',
        notes: voidReason ? `REASON: ${voidReason}` : '',
        arabicNumerals: false,
      });
      if (usbPrinted) return;
    }

    const typeBannerBg = ticketType === 'VOID' ? '#dc2626' : (ticketType === 'REPRINT' ? '#0284c7' : (round > 1 ? '#d97706' : '#0f172a'));
    const typeBannerText = ticketType === 'VOID' ? '*** VOID / CANCELLED KOT ***' : (ticketType === 'REPRINT' ? '*** DUPLICATE / REPRINT ***' : (round > 1 ? `RUNNING ORDER — ROUND #${round}` : 'NEW ORDER'));

    const linesHtml = items.map(i => `
      <div style="margin: 8px 0; border-bottom: 1px dashed #d1d5db; padding-bottom: 6px;">
        <div style="display:flex; justify-content:space-between; align-items:flex-start; font-size: 15px; font-weight: 800;">
          <span style="font-size: 17px; min-width: 34px;">[ ${i.qty}x ]</span>
          <span style="flex:1; margin-left: 8px;">${i.name}</span>
          ${i.course ? `<span style="font-size: 10px; padding: 2px 6px; border: 1px solid #000; border-radius: 4px; text-transform: uppercase;">${i.course}</span>` : ''}
        </div>
        ${i.notes ? `<div style="font-size: 12px; font-weight: 700; color: #111; margin-left: 42px; margin-top: 3px; font-style: italic;">👉 NOTE: ${i.notes}</div>` : ''}
      </div>
    `).join('');

    const kotHtml = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>KOT #${displayNum}</title><style>
      *{margin:0;padding:0;box-sizing:border-box;}
      body{font-family:'Courier New', monospace; padding: 10px; width: 280px; color: #000;}
      .banner{text-align:center; padding: 4px; background: ${typeBannerBg}; color: #fff; font-weight: 900; font-size: 12px; border-radius: 4px; margin-bottom: 8px;}
      .header{text-align:center; margin-bottom: 6px;}
      .kot-no{font-size: 20px; font-weight: 900; letter-spacing: -0.5px;}
      .meta{font-size: 12px; font-weight: 700; margin: 4px 0; display:flex; justify-content:space-between;}
      .table-box{font-size: 24px; font-weight: 900; text-align: center; border: 2px solid #000; padding: 4px; border-radius: 6px; margin: 6px 0;}
      hr{border:none; border-top: 2px solid #000; margin: 8px 0;}
      @media print{@page{size:80mm auto; margin:0;}}
    </style></head><body>
      <div class="banner">${typeBannerText}</div>
      <div class="header">
        <div class="kot-no">KOT #${displayNum}</div>
        <div style="font-size:11px;">${dateStr} · ${timeStr}</div>
      </div>
      <div class="table-box">
        TABLE ${table?.number || ''} <span style="font-size:13px; font-weight:bold;">(${floorName})</span>
      </div>
      <div class="meta">
        <span>Covers (PAX): <b>${table?.seats || 2}</b></span>
        <span>Server: <b>${serverName}</b></span>
      </div>
      ${voidReason ? `<div style="background:#fee2e2; border:1px solid #b91c1c; color:#b91c1c; font-size:12px; font-weight:800; padding:4px; margin:6px 0; text-align:center;">VOID REASON: ${voidReason}</div>` : ''}
      <hr/>
      ${linesHtml}
      <hr/>
      <div style="text-align:center; font-size:10px; font-weight:bold; margin-top:8px;">TOTAL ITEMS SENT: ${items.reduce((s, x) => s + x.qty, 0)}</div>
    </body></html>`;

    const printFrame = document.createElement('iframe');
    printFrame.style.cssText = 'position:fixed;inset:0;width:100%;height:100%;border:none;z-index:9999;background:#fff;';
    document.body.appendChild(printFrame);
    const doc = printFrame.contentDocument || printFrame.contentWindow?.document;
    if (doc) {
      doc.open();
      doc.write(kotHtml);
      doc.close();
      setTimeout(() => {
        printFrame.contentWindow?.focus();
        printFrame.contentWindow?.print();
        setTimeout(() => {
          if (document.body.contains(printFrame)) document.body.removeChild(printFrame);
        }, 1000);
      }, 400);
    } else {
      document.body.removeChild(printFrame);
    }
  };

  const sendKOT = (shouldPrint: boolean) => {
    if (!orderTableId || unsentItems.length === 0) return;
    const round = (kotRounds[orderTableId] || 0) + 1;
    const currentSeq = parseInt(localStorage.getItem(KOT_SEQ_KEY) || '100', 10) + 1;
    localStorage.setItem(KOT_SEQ_KEY, String(currentSeq));

    setTableTimings(prev => prev[orderTableId]?.firstKotAt
      ? prev
      : { ...prev, [orderTableId]: { ...prev[orderTableId], firstKotAt: Date.now() } });
    
    const itemsForThisRound = unsentItems;
    setOrders(prev => ({
      ...prev,
      [orderTableId]: (prev[orderTableId] || []).map(i =>
        !i.sentToKitchen ? { ...i, sentToKitchen: true, kotRound: round } : i
      ),
    }));
    setKotRounds(prev => ({ ...prev, [orderTableId]: round }));

    const t = tables.find(tt => tt.id === orderTableId);
    const floorObj = floors.find(f => f.id === t?.floorId);
    const serverName = t?.attendedBy || authUser?.name || 'Staff';

    const historyEntry: KOTHistoryEntry = {
      id: `kot-${Date.now()}`,
      kotNumber: currentSeq,
      round,
      tableId: orderTableId,
      tableNumber: t?.number || '',
      floorName: floorObj?.name || '',
      serverName,
      timestamp: Date.now(),
      items: itemsForThisRound.map(i => ({ name: i.name, qty: i.qty, notes: i.notes, course: i.course })),
    };
    setKotHistory(prev => [historyEntry, ...prev]);
    setLastKOT({ tableId: orderTableId, round, items: itemsForThisRound });

    const noteChunk = itemsForThisRound.map(i =>
      `${i.qty}x ${i.name}${i.course ? ` [${i.course.toUpperCase()}]` : ''}${i.notes ? ` (${i.notes})` : ''}`
    ).join(', ');

    const existingToken = kitchenTokens[orderTableId];
    const jwt = getStoredToken();
    const authHeader = jwt ? { Authorization: `Bearer ${jwt}` } : {};

    if (existingToken) {
      fetch(`${BACKEND}/tokens/${existingToken}/items`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...authHeader },
        body: JSON.stringify({ append_note: noteChunk }),
      }).catch(() => {});
    } else {
      fetch(`${BACKEND}/tokens/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader },
        body: JSON.stringify({
          customer_name: `Table ${t?.number}`,
          order_note: noteChunk,
          outlet_id: outletId,
        }),
      })
        .then(r => r.json())
        .then(data => {
          if (data?.token) setKitchenTokens(prev => ({ ...prev, [orderTableId]: data.token }));
        })
        .catch(() => {});
    }

    if (shouldPrint) {
      printKOTTicket({
        round,
        items: itemsForThisRound,
        table: t,
        kotNumber: currentSeq,
        ticketType: round > 1 ? 'RUNNING' : 'NEW',
      });
    }
  };

  const handleConfirmVoid = () => {
    if (!voidPrompt || !orderTableId) return;
    const { itemId, name, qty, round } = voidPrompt;
    const t = tables.find(tt => tt.id === orderTableId);
    const floorObj = floors.find(f => f.id === t?.floorId);

    setOrders(prev => ({
      ...prev,
      [orderTableId]: (prev[orderTableId] || []).filter(i => i.id !== itemId),
    }));

    const currentSeq = parseInt(localStorage.getItem(KOT_SEQ_KEY) || '100', 10) + 1;
    localStorage.setItem(KOT_SEQ_KEY, String(currentSeq));

    const voidEntry: KOTHistoryEntry = {
      id: `void-${Date.now()}`,
      kotNumber: currentSeq,
      round,
      tableId: orderTableId,
      tableNumber: t?.number || '',
      floorName: floorObj?.name || '',
      serverName: t?.attendedBy || authUser?.name || 'Staff',
      timestamp: Date.now(),
      items: [{ name, qty }],
      isVoid: true,
      voidReason,
    };
    setKotHistory(prev => [voidEntry, ...prev]);

    const existingToken = kitchenTokens[orderTableId];
    if (existingToken) {
      const jwt = getStoredToken();
      fetch(`${BACKEND}/tokens/${existingToken}/items`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}) },
        body: JSON.stringify({ append_note: `❌ CANCELLED: ${qty}x ${name} (Reason: ${voidReason})` }),
      }).catch(() => {});
    }

    printKOTTicket({
      round,
      items: [{ name, qty }],
      table: t,
      kotNumber: currentSeq,
      ticketType: 'VOID',
      voidReason,
    });

    setVoidPrompt(null);
  };

  const markServed = (tableId: string) => {
    setTableServedStatus(prev => ({ ...prev, [tableId]: true }));
  };

  const markVacant = (tableId: string) => {
    setOrders(prev => { const n = { ...prev }; delete n[tableId]; return n; });
    setKotRounds(prev => { const n = { ...prev }; delete n[tableId]; return n; });
    setTableServedStatus(prev => { const n = { ...prev }; delete n[tableId]; return n; });
    setTableKitchenStatus(prev => { const n = { ...prev }; delete n[tableId]; return n; });
    setKitchenTokens(prev => { const n = { ...prev }; delete n[tableId]; return n; });
    setTableTimings(prev => { const n = { ...prev }; delete n[tableId]; return n; });
    updateTable(tableId, { attendedBy: undefined });
    if (orderTableId === tableId) setOrderTableId(null);
  };

  const handleCheckout = () => {
    if (!orderTableId || !orderTable || orderItems.length === 0) return;
    onCheckout?.({ tableId: orderTableId, tableNumber: orderTable.number, items: orderItems });
    setOrderTableId(null);
  };

  const categories = useMemo(() => {
    const set = new Set<string>();
    products.forEach(p => { if (p.category) set.add(p.category); });
    return Array.from(set);
  }, [products]);

  const filteredProducts = useMemo(() => {
    const q = productSearch.toLowerCase();
    return products.filter(p => {
      const matchQuery = !q || p.name.toLowerCase().includes(q) || p.barcode?.includes(q);
      const matchCat = selectedCategory === 'all' || p.category === selectedCategory;
      return matchQuery && matchCat;
    });
  }, [products, productSearch, selectedCategory]);

  const statusColor = (s: TableStatus) =>
    s === 'occupied' ? '#f59e0b' : s === 'reserved' ? '#6366f1' : '#10b981';

  return (
    <div style={{ display: 'flex', height: '100%', overflow: 'hidden', fontFamily: 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif' }}>
      <style>{readyPulseStyle}</style>

      {/* ── Floor plan canvas ── */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>

        {/* Floor Tabs Header */}
        <div style={{ padding: '8px 16px', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', background: '#f8fafc' }}>
          {floors.map(f => (
            <div key={f.id} style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {editingFloorId === f.id ? (
                <input
                  autoFocus
                  defaultValue={f.name}
                  onBlur={e => { renameFloor(f.id, e.target.value.trim() || f.name); setEditingFloorId(null); }}
                  onKeyDown={e => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
                  style={{ padding: '5px 10px', borderRadius: 8, border: '1.5px solid #0284c7', fontSize: 12, fontWeight: 700, width: 110, outline: 'none' }}
                />
              ) : (
                <>
                  <button
                    onClick={() => setActiveFloorId(f.id)}
                    onDoubleClick={() => canManageFloors && setEditingFloorId(f.id)}
                    style={{
                      padding: '6px 14px', borderRadius: 8, border: activeFloorId === f.id ? '1px solid #3b82f6' : '1px solid #e2e8f0', cursor: 'pointer',
                      background: activeFloorId === f.id ? '#3b82f6' : '#fff',
                      color: activeFloorId === f.id ? '#fff' : '#64748b',
                      fontSize: 12, fontWeight: 800, transition: 'all 0.12s ease',
                      boxShadow: activeFloorId === f.id ? '0 2px 8px rgba(59,130,246,0.3)' : 'none',
                    }}
                  >
                    🏢 {f.name}
                  </button>
                  {canManageFloors && (
                    <button
                      onClick={() => setEditingFloorId(f.id)}
                      title="Rename floor"
                      style={{
                        width: 22, height: 22, borderRadius: 6, border: 'none', cursor: 'pointer',
                        background: 'transparent', color: '#94a3b8',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}
                      onMouseEnter={e => { e.currentTarget.style.background = '#e2e8f0'; e.currentTarget.style.color = '#334155'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#94a3b8'; }}
                    >
                      <Pencil size={11} />
                    </button>
                  )}
                </>
              )}
            </div>
          ))}
          {canManageFloors && (
            <button onClick={addFloor} style={{ padding: '6px 12px', borderRadius: 8, border: '1.5px dashed #cbd5e1', background: 'transparent', color: '#64748b', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
              + Add Floor
            </button>
          )}
          {canManageFloors && mode === 'design' && floors.length > 1 && (
            <button onClick={() => deleteFloor(activeFloorId)} style={{ padding: '6px 12px', borderRadius: 8, border: '1px solid #fecaca', background: '#fff5f5', color: '#dc2626', fontSize: 11, fontWeight: 700, cursor: 'pointer', marginLeft: 'auto' }}>
              🗑 Delete Floor
            </button>
          )}
        </div>

        {/* Action Toolbar */}
        <div style={{ padding: '10px 16px', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', background: '#fff', boxShadow: '0 1px 2px rgba(15,23,42,0.03)' }}>
          {canAccessKitchenDashboard && (
            <>
              <button
                onClick={() => {
                  if (!outletKitchenToken) { alert('Kitchen isn\'t linked for this outlet yet.'); return; }
                  window.open(`/kitchen?outlet_token=${outletKitchenToken}`, '_blank');
                }}
                title={outletKitchenToken ? 'Open Kitchen Dashboard' : 'Kitchen token not loaded yet'}
                style={{
                  ...btn, background: '#0f172a', color: '#fff', borderColor: '#0f172a',
                  opacity: outletKitchenToken ? 1 : 0.5,
                }}
              >
                <ChefHat size={13} color="#38bdf8" /> Kitchen
              </button>
              <div style={{ width: 1, height: 20, background: '#e2e8f0' }} />
            </>
          )}

          {/* Auto-Print KOT Toggle */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 10px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#f8fafc' }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: '#334155' }}>🖨 Auto-Print KOT</span>
            <button
              onClick={() => {
                const next = !autoPrintKOT;
                setAutoPrintKOT(next);
                localStorage.setItem(KOT_AUTO_PRINT_KEY, String(next));
              }}
              style={{
                width: 36, height: 20, borderRadius: 10, border: 'none', cursor: 'pointer',
                background: autoPrintKOT ? '#0284c7' : '#cbd5e1', position: 'relative', flexShrink: 0,
                transition: 'background 0.2s',
              }}
            >
              <div style={{
                width: 14, height: 14, borderRadius: '50%', background: '#fff',
                position: 'absolute', top: 3, left: autoPrintKOT ? 19 : 3,
                transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
              }} />
            </button>
          </div>

          {/* Printer Settings Dialog */}
          <div style={{ position: 'relative' }}>
            <button
              onClick={() => setShowPrintSettings(s => !s)}
              title="Printer settings"
              style={{
                ...btn, padding: '6px 10px',
                background: showPrintSettings ? '#f0f9ff' : '#fff',
                borderColor: showPrintSettings ? '#0284c7' : '#e2e8f0',
                color: showPrintSettings ? '#0284c7' : '#334155',
              }}
            >⚙</button>
            {showPrintSettings && (
              <div style={{
                position: 'absolute', top: '115%', left: 0, zIndex: 500,
                background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12,
                padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 10,
                width: 270, boxShadow: '0 10px 30px rgba(15,23,42,0.12)',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div>
                    <div style={{ fontSize: 11.5, fontWeight: 700, color: '#0f172a' }}>Print KOT via USB</div>
                    <div style={{ fontSize: 9.5, color: '#64748b' }}>
                      {usbConnected ? `Connected: ${getUSBPrinterName() || 'USB'}` : 'No USB printer'}
                    </div>
                  </div>
                  <button onClick={() => {
                    const next = !useUSBPrinter;
                    setUseUSBPrinter(next);
                    localStorage.setItem('cloudairambo_kot_usb_print', String(next));
                    if (next) { setUseBTPrinter(false); localStorage.setItem('cloudairambo_kot_bt_print', 'false'); }
                  }} style={{
                    width: 36, height: 20, borderRadius: 10, border: 'none', cursor: 'pointer',
                    background: useUSBPrinter ? '#0284c7' : '#cbd5e1', position: 'relative', flexShrink: 0,
                  }}>
                    <div style={{ width: 14, height: 14, borderRadius: '50%', background: '#fff', position: 'absolute', top: 3, left: useUSBPrinter ? 19 : 3, transition: 'left 0.2s' }} />
                  </button>
                </div>
                {useUSBPrinter && !usbConnected && (
                  <button onClick={async () => { const ok = await connectUSBPrinter(); setUsbConnected(ok); }} style={{
                    width: '100%', fontSize: 11, fontWeight: 700, padding: '6px 8px', borderRadius: 6,
                    border: '1px solid #bae6fd', background: '#f0f9ff', color: '#0284c7', cursor: 'pointer',
                  }}>🔌 Connect USB Printer</button>
                )}

                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div>
                    <div style={{ fontSize: 11.5, fontWeight: 700, color: '#0f172a' }}>Print KOT via Bluetooth</div>
                    <div style={{ fontSize: 9.5, color: '#64748b' }}>
                      {btConnected ? `Connected: ${getBTPrinterName() || 'BT'}` : 'No BT printer'}
                    </div>
                  </div>
                  <button onClick={() => {
                    const next = !useBTPrinter;
                    setUseBTPrinter(next);
                    localStorage.setItem('cloudairambo_kot_bt_print', String(next));
                    if (next) { setUseUSBPrinter(false); localStorage.setItem('cloudairambo_kot_usb_print', 'false'); }
                  }} style={{
                    width: 36, height: 20, borderRadius: 10, border: 'none', cursor: 'pointer',
                    background: useBTPrinter ? '#0284c7' : '#cbd5e1', position: 'relative', flexShrink: 0,
                  }}>
                    <div style={{ width: 14, height: 14, borderRadius: '50%', background: '#fff', position: 'absolute', top: 3, left: useBTPrinter ? 19 : 3, transition: 'left 0.2s' }} />
                  </button>
                </div>
                {useBTPrinter && (
                  <button onClick={async () => { const char = await connectBluetoothPrinter(); setBtConnected(!!char); }} style={{
                    width: '100%', fontSize: 11, fontWeight: 700, padding: '6px 8px', borderRadius: 6,
                    border: '1px solid #bae6fd', background: '#f0f9ff', color: '#0284c7', cursor: 'pointer',
                  }}>🔵 Connect Bluetooth Printer</button>
                )}
              </div>
            )}
          </div>

          <div style={{ width: 1, height: 20, background: '#e2e8f0' }} />

          {/* Mode Switcher */}
          <div style={{ display: 'flex', border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden', background: '#f8fafc' }}>
            <button onClick={() => setMode('order')} style={{
              ...btn, border: 'none', borderRadius: 0,
              background: mode === 'order' ? '#3b82f6' : 'transparent',
              color: mode === 'order' ? '#fff' : '#64748b',
            }}><Utensils size={13} /> Order Mode</button>
            {canEditTableLayout && (
              <button onClick={() => setMode('design')} style={{
                ...btn, border: 'none', borderRadius: 0, borderLeft: '1px solid #e2e8f0',
                background: mode === 'design' ? '#3b82f6' : 'transparent',
                color: mode === 'design' ? '#fff' : '#64748b',
              }}><Move size={13} /> Design Layout</button>
            )}
          </div>

          {mode === 'design' && (
            <>
              <button style={btn} onClick={() => addTable('square')}><Square size={13} /> + Square</button>
              <button style={btn} onClick={() => addTable('round')}><CircleIcon size={13} /> + Round</button>
              <div style={{ width: 1, height: 20, background: '#e2e8f0' }} />
              {(['kitchen', 'counter', 'entrance', 'restroom', 'bar'] as const).map(type => (
                <button key={type} style={btn} onClick={() => addLandmark(type)}>
                  {LANDMARK_META[type].emoji} {LANDMARK_META[type].defaultLabel}
                </button>
              ))}
              {selectedTableId && (
                <>
                  <div style={{ width: 1, height: 20, background: '#e2e8f0' }} />
                  <input
                    style={{ width: 55, padding: '5px 8px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 12 }}
                    value={tables.find(t => t.id === selectedTableId)?.number || ''}
                    onChange={e => updateTable(selectedTableId, { number: e.target.value })}
                    placeholder="No."
                  />
                  <input
                    style={{ width: 120, padding: '5px 8px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 12 }}
                    value={tables.find(t => t.id === selectedTableId)?.label || ''}
                    onChange={e => updateTable(selectedTableId, { label: e.target.value })}
                    placeholder="Name"
                  />
                  <div style={{ display: 'flex', border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden' }}>
                    <button onClick={() => {
                      const t = tables.find(tt => tt.id === selectedTableId);
                      updateTable(selectedTableId, { rotation: ((t?.rotation || 0) - 15 + 360) % 360 });
                    }} style={{ padding: '6px 8px', border: 'none', background: '#fff', cursor: 'pointer' }}><RotateCcw size={13} /></button>
                    <button onClick={() => {
                      const t = tables.find(tt => tt.id === selectedTableId);
                      updateTable(selectedTableId, { rotation: ((t?.rotation || 0) + 15) % 360 });
                    }} style={{ padding: '6px 8px', border: 'none', borderLeft: '1px solid #e2e8f0', background: '#fff', cursor: 'pointer' }}><RotateCw size={13} /></button>
                  </div>
                  <button style={{ ...btn, color: '#ef4444', borderColor: '#fecaca', background: '#fef2f2' }} onClick={() => deleteTable(selectedTableId)}>
                    <Trash2 size={13} /> Remove
                  </button>
                </>
              )}
            </>
          )}

          {mode === 'order' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginLeft: 'auto', fontSize: 11, fontWeight: 700, color: '#64748b' }}>
              <button
                onClick={() => setShowAllReservationsModal(true)}
                title="Open Reservation Book"
                style={{
                  ...btn, background: '#f5f3ff', color: '#6366f1', borderColor: '#ddd6fe',
                }}
              >
                <Calendar size={13} /> Reservation Book
              </button>
              <button
                onClick={() => setShowHistoryModal(true)}
                title="View All KOT History & Reprints"
                style={{
                  ...btn, background: '#f8fafc', color: '#0284c7', borderColor: '#bae6fd',
                }}
              >
                <History size={13} /> KOT History {kotHistory.length > 0 ? `(${kotHistory.length})` : ''}
              </button>
              <div style={{
                display: 'flex', alignItems: 'center', gap: 6, padding: '5px 10px',
                borderRadius: 8, border: '1px solid #e2e8f0', background: '#f8fafc',
              }}>
                <Clock size={13} color="#64748b" />
                <span style={{ fontSize: 12, fontWeight: 800, color: '#0f172a', fontVariantNumeric: 'tabular-nums' }}>
                  {now.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                </span>
              </div>
              <span><span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: '#10b981', marginRight: 4 }} />Empty</span>
              <span><span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: '#f59e0b', marginRight: 4 }} />Occupied</span>
              <span><span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: '#6366f1', marginRight: 4 }} />Reserved</span>
            </div>
          )}
        </div>

        {/* Canvas Layout Area */}
        <div
          ref={canvasRef}
          onClick={() => { setSelectedTableId(null); setSelectedLandmarkId(null); }}
          style={{
            flex: 1, position: 'relative', overflow: 'auto',
            background: '#fafbfc',
            backgroundImage: 'radial-gradient(#cbd5e1 1px, transparent 1px)',
            backgroundSize: '24px 24px',
          }}
        >
          {tables.length === 0 && (
            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', color: '#94a3b8', gap: 8 }}>
              <Utensils size={36} style={{ opacity: 0.3 }} />
              <div style={{ fontSize: 13, fontWeight: 700 }}>No tables yet</div>
              {canEditTableLayout && <div style={{ fontSize: 11 }}>Switch to Design Layout to add your first table</div>}
            </div>
          )}

          {tables.filter(t => t.floorId === activeFloorId).map(t => {
            const status = tableStatus(t.id);
            const color = statusColor(status);
            const chairCount = t.seats;
            const chairs = [];
            const chairSize = 14;

            if (t.shape === 'round') {
              const radius = Math.max(t.width, t.height) / 2 + 14;
              for (let i = 0; i < chairCount; i++) {
                const angle = (i / chairCount) * Math.PI * 2 - Math.PI / 2;
                chairs.push({
                  x: t.width / 2 + radius * Math.cos(angle) - chairSize / 2,
                  y: t.height / 2 + radius * Math.sin(angle) - chairSize / 2
                });
              }
            } else {
              const perSideBase = Math.floor(chairCount / 4);
              let remainder = chairCount % 4;
              const sides = [0, 1, 2, 3].map(s => perSideBase + (remainder-- > 0 ? 1 : 0));
              const gap = 10;
              for (let i = 0; i < sides[0]; i++) chairs.push({ x: (t.width / (sides[0] + 1)) * (i + 1) - chairSize / 2, y: -gap - chairSize });
              for (let i = 0; i < sides[1]; i++) chairs.push({ x: (t.width / (sides[1] + 1)) * (i + 1) - chairSize / 2, y: t.height + gap });
              for (let i = 0; i < sides[2]; i++) chairs.push({ x: -gap - chairSize, y: (t.height / (sides[2] + 1)) * (i + 1) - chairSize / 2 });
              for (let i = 0; i < sides[3]; i++) chairs.push({ x: t.width + gap, y: (t.height / (sides[3] + 1)) * (i + 1) - chairSize / 2 });
            }

            return (
              <div
                key={t.id}
                onMouseDown={e => onTableMouseDown(e, t)}
                onClick={e => { e.stopPropagation(); handleTableClick(t); }}
                onContextMenu={e => { e.preventDefault(); if (mode === 'order') openReserveModal(t.id); }}
                style={{
                  position: 'absolute', left: t.x, top: t.y, width: t.width, height: t.height,
                  cursor: mode === 'design' ? 'grab' : 'pointer',
                  transform: t.rotation ? `rotate(${t.rotation}deg)` : undefined,
                  transformOrigin: 'center center',
                }}
              >
                {/* Chairs */}
                {chairs.map((c, i) => (
                  <div key={i} style={{
                    position: 'absolute', left: c.x, top: c.y, width: chairSize, height: chairSize,
                    borderRadius: 4, background: '#fff', border: '1.5px solid #cbd5e1',
                  }} />
                ))}

                {/* Table Body */}
                <div style={{
                  width: '100%', height: '100%',
                  borderRadius: t.shape === 'round' ? '50%' : 14,
                  background: status === 'reserved' ? '#f5f3ff' : '#fff',
                  border: `${status === 'empty' ? '1.5px solid #e2e8f0' : `2px solid ${color}`}`,
                  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                  boxShadow: t.id === selectedTableId
                    ? '0 0 0 3px rgba(2,132,199,0.25), 0 8px 20px rgba(15,23,42,0.12)'
                    : '0 1px 3px rgba(15,23,42,0.06)',
                  position: 'relative', transition: 'all 0.15s ease',
                }}>
                  <div style={{
                    position: 'absolute', top: 7, left: 7, width: 7, height: 7,
                    borderRadius: '50%', background: color,
                    boxShadow: status !== 'empty' ? `0 0 0 3px ${color}22` : 'none',
                  }} />

                  <div style={{ fontSize: 16, fontWeight: 900, color: status === 'reserved' ? '#4338ca' : '#0f172a', letterSpacing: '-0.02em', lineHeight: 1.1 }}>
                    {t.number}
                  </div>
                  {t.label && (
                    <div style={{ fontSize: 9.5, fontWeight: 700, color: '#64748b', maxWidth: t.width - 16, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {t.label}
                    </div>
                  )}
                  <div style={{ fontSize: 10, color: '#64748b', display: 'flex', alignItems: 'center', gap: 2, fontWeight: 700, marginTop: 1 }}>
                    <Users size={10} /> {t.seats}
                  </div>

                  {mode === 'order' && status !== 'empty' && (
                    <div style={{
                      fontSize: 8.5, fontWeight: 800, marginTop: 3, padding: '1px 6px', borderRadius: 6,
                      background: t.attendedBy ? '#dcfce7' : '#fee2e2',
                      color: t.attendedBy ? '#15803d' : '#b91c1c',
                      maxWidth: t.width - 16, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                      {t.attendedBy ? `👤 ${t.attendedBy}` : '⚠ Unattended'}
                    </div>
                  )}

                  {status === 'occupied' && (
                    <div style={{ fontSize: 10, fontWeight: 900, color: '#92400e', marginTop: 3, background: '#fef3c7', padding: '1px 7px', borderRadius: 12 }}>
                      {sym}{(orders[t.id] || []).reduce((s, i) => s + i.qty * i.rate, 0).toFixed(0)}
                    </div>
                  )}
                </div>

                {/* Intelligent Reservation Indicators */}
                {(() => {
                  const { active, next } = getTableActiveOrNextReservation(t, now);
                  if (active && status !== 'occupied') {
                    return (
                      <div
                        onClick={e => { e.stopPropagation(); openReserveModal(t.id, active.id); }}
                        title={`Reserved: ${active.name} (${active.partySize}p) at ${active.time} — Click to manage`}
                        style={{
                          position: 'absolute', bottom: -12, left: '50%', transform: 'translateX(-50%)',
                          background: 'linear-gradient(135deg, #6366f1, #4f46e5)', color: '#fff',
                          borderRadius: 20, padding: '3px 9px', fontSize: 9.5, fontWeight: 900,
                          boxShadow: '0 2px 8px rgba(99,102,241,0.45)', whiteSpace: 'nowrap',
                          cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, zIndex: 10,
                        }}
                      >
                        <span>📅</span>
                        <span>{active.time} · {active.name}</span>
                      </div>
                    );
                  }
                  if (next && status === 'empty') {
                    return (
                      <div
                        onClick={e => { e.stopPropagation(); openReserveModal(t.id, next.id); }}
                        title={`Upcoming: ${next.name} at ${next.time}`}
                        style={{
                          position: 'absolute', bottom: -11, left: '50%', transform: 'translateX(-50%)',
                          background: '#f1f5f9', color: '#475569', border: '1px solid #cbd5e1',
                          borderRadius: 14, padding: '2px 7px', fontSize: 9, fontWeight: 800,
                          whiteSpace: 'nowrap', cursor: 'pointer', zIndex: 10,
                        }}
                      >
                        🕒 Next: {next.time}
                      </div>
                    );
                  }
                  return null;
                })()}

                {/* Delay / Ready Status Tags */}
                {tableKitchenStatus[t.id] === 'waiting' && (() => {
                  const waitMins = tableTimings[t.id]?.firstKotAt
                    ? Math.round((Date.now() - tableTimings[t.id]!.firstKotAt!) / 60000)
                    : 0;
                  const isDelayed = waitMins >= 20;
                  return (
                    <div style={{
                      position: 'absolute', top: -10, right: -10, display: 'flex', alignItems: 'center', gap: 4,
                      background: isDelayed ? '#dc2626' : '#f59e0b', color: '#fff', borderRadius: 20,
                      padding: '3px 8px', fontSize: 10, fontWeight: 800,
                      boxShadow: isDelayed ? '0 2px 8px rgba(220,38,38,0.4)' : '0 2px 6px rgba(245,158,11,0.3)',
                    }}>
                      <span>{isDelayed ? '⚠️' : '⏳'}</span>
                      <span>{isDelayed ? `${waitMins}m Delay` : 'Cooking'}</span>
                    </div>
                  );
                })()}

                {tableKitchenStatus[t.id] === 'ready' && !tableServedStatus[t.id] && (
                  <div
                    onClick={e => { e.stopPropagation(); markServed(t.id); }}
                    style={{
                      position: 'absolute', top: -10, right: -10, display: 'flex', alignItems: 'center', gap: 4,
                      background: '#10b981', color: '#fff', borderRadius: 20,
                      padding: '3px 9px', fontSize: 10, fontWeight: 800, cursor: 'pointer',
                      animation: 'tableReadyPulse 1.4s ease-in-out infinite',
                    }}
                  >
                    <span>🔔</span>
                    <span>Ready</span>
                  </div>
                )}

                {tableServedStatus[t.id] && (
                  <div style={{
                    position: 'absolute', top: -10, right: -10, display: 'flex', alignItems: 'center', gap: 4,
                    background: '#6366f1', color: '#fff', borderRadius: 20,
                    padding: '3px 8px', fontSize: 10, fontWeight: 800,
                  }}>
                    <span>🍽️</span>
                    <span>Served</span>
                  </div>
                )}

                {mode === 'design' && t.id === selectedTableId && (
                  <div
                    onMouseDown={e => onResizeMouseDown(e, t)}
                    style={{
                      position: 'absolute', right: -6, bottom: -6, width: 14, height: 14,
                      borderRadius: '50%', background: '#0284c7', border: '2px solid #fff',
                      cursor: 'nwse-resize',
                    }}
                  />
                )}
              </div>
            );
          })}

          {landmarks.filter(l => l.floorId === activeFloorId).map(l => {
            const meta = LANDMARK_META[l.type];
            return (
              <div
                key={l.id}
                onMouseDown={e => onLandmarkMouseDown(e, l)}
                onClick={e => { e.stopPropagation(); if (mode === 'design') setSelectedLandmarkId(l.id); }}
                style={{
                  position: 'absolute', left: l.x, top: l.y,
                  cursor: mode === 'design' ? 'grab' : 'default',
                  display: 'flex', alignItems: 'center', gap: 6,
                  background: '#fff', border: `1.5px solid ${meta.color}`,
                  borderRadius: 20, padding: '5px 12px 5px 8px',
                  boxShadow: l.id === selectedLandmarkId ? `0 0 0 3px ${meta.color}33` : '0 1px 3px rgba(15,23,42,0.06)',
                  transform: l.rotation ? `rotate(${l.rotation}deg)` : undefined,
                  transformOrigin: 'center center',
                }}
              >
                <span>{meta.emoji}</span>
                <span style={{ fontSize: 11, fontWeight: 800, color: meta.color }}>{l.label}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Order Drawer (Executive Slate Theme) ── */}
      {orderTable && (
        <div style={{
          width: drawerWidth, minWidth: 520, maxWidth: 1100,
          borderLeft: '1px solid #e2e8f0', background: '#fff',
          display: 'flex', flexDirection: 'column', position: 'relative', flexShrink: 0,
          boxShadow: '-4px 0 24px rgba(15,23,42,0.06)', zIndex: 40,
        }}>
          {/* Resize Handle */}
          <div
            onMouseDown={e => {
              e.preventDefault();
              drawerResizeRef.current = { startX: e.clientX, origW: drawerWidth };
              setIsDraggingDrawer(true);
              document.body.style.userSelect = 'none';
            }}
            title="Drag to resize drawer"
            style={{
              position: 'absolute', top: 0, bottom: 0, left: -4, width: 8,
              cursor: 'ew-resize', zIndex: 50,
            }}
          >
            <div style={{
              position: 'absolute', top: '50%', left: 3, width: 3, height: isDraggingDrawer ? '100%' : 44,
              transform: 'translateY(-50%)', borderRadius: 3,
              background: '#0284c7', opacity: isDraggingDrawer ? 1 : 0.3,
            }} />
          </div>

          {/* Drawer Header (POSView Signature Gradient) */}
          <div style={{
            padding: '16px 18px', background: 'linear-gradient(135deg,#3b82f6,#9333ea)', color: '#fff',
            display: 'flex', flexDirection: 'column', gap: 12, borderBottom: '1px solid rgba(255,255,255,0.15)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{
                  background: 'rgba(255,255,255,0.2)', border: '1px solid rgba(255,255,255,0.35)',
                  borderRadius: 10, padding: '5px 12px', fontSize: 14, fontWeight: 900,
                  color: '#fff', letterSpacing: '-0.02em', display: 'flex', alignItems: 'center', gap: 6
                }}>
                  <span>🍽️ Table {orderTable.number}</span>
                </div>
                <span style={{ fontSize: 11.5, color: '#e0e7ff', fontWeight: 600 }}>
                  ({orderTable.seats} Seats)
                </span>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                {tableStatus(orderTable.id) !== 'empty' && (
                  <button
                    onClick={() => setVacantConfirm({ tableId: orderTable.id })}
                    style={{
                      background: 'rgba(239,68,68,0.25)', border: '1px solid rgba(239,68,68,0.4)',
                      color: '#fee2e2', borderRadius: 8, padding: '5px 10px', fontSize: 11, fontWeight: 800,
                      cursor: 'pointer', transition: 'all 0.12s ease',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = '#dc2626'; e.currentTarget.style.color = '#fff'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'rgba(239,68,68,0.25)'; e.currentTarget.style.color = '#fee2e2'; }}
                  >
                    Clear / Vacant
                  </button>
                )}
                {tableStatus(orderTable.id) !== 'occupied' && (
                  <button
                    onClick={() => openReserveModal(orderTable.id)}
                    style={{
                      background: 'rgba(255,255,255,0.18)', border: '1px solid rgba(255,255,255,0.3)',
                      color: '#fff', borderRadius: 8, padding: '5px 10px', fontSize: 11, fontWeight: 800,
                      cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4,
                    }}
                  >
                    <Calendar size={12} />
                    <span>Book / Reserve</span>
                  </button>
                )}
                <button
                  onClick={() => setOrderTableId(null)}
                  style={{
                    background: 'transparent', border: 'none', color: '#e0e7ff', cursor: 'pointer',
                    width: 28, height: 28, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.color = '#fff'; }}
                  onMouseLeave={e => { e.currentTarget.style.color = '#e0e7ff'; }}
                >
                  <X size={18} />
                </button>
              </div>
            </div>

            {/* Server / Waiter Assignment */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'rgba(255,255,255,0.15)', borderRadius: 8, padding: '6px 12px', border: '1px solid rgba(255,255,255,0.25)' }}>
              <span style={{ fontSize: 13, color: orderTable.attendedBy ? '#86efac' : '#fde047' }}>
                {orderTable.attendedBy ? '🧑‍🍳' : '⚠️'}
              </span>
              <input
                value={orderTable.attendedBy || ''}
                onChange={e => updateTable(orderTable.id, { attendedBy: e.target.value })}
                placeholder="Assign server / waiter name..."
                style={{
                  background: 'transparent', border: 'none', outline: 'none',
                  color: '#fff',
                  fontSize: 12, fontWeight: 700, width: '100%',
                }}
              />
              {tableTimings[orderTable.id]?.firstKotAt && (
                <div style={{ fontSize: 10, color: '#e0e7ff', fontWeight: 700, whiteSpace: 'nowrap', borderLeft: '1px solid rgba(255,255,255,0.3)', paddingLeft: 8 }}>
                  ⏱️ {Math.round((Date.now() - tableTimings[orderTable.id]!.firstKotAt!) / 60000)}m
                </div>
              )}
            </div>

            {tableKitchenStatus[orderTable.id] === 'ready' && !tableServedStatus[orderTable.id] && (
              <button
                onClick={() => markServed(orderTable.id)}
                style={{
                  width: '100%', background: '#10b981', color: '#fff', border: 'none', borderRadius: 8,
                  padding: '7px 12px', fontSize: 11.5, fontWeight: 800, cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                }}
              >
                🔔 Order Ready! Mark Delivered / Served
              </button>
            )}
          </div>

          {/* Active Table Cart Summary Bar */}
          <div style={{ padding: '12px 16px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
            <button
              onClick={() => setShowOrderPopup(true)}
              style={{
                width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '12px 16px', borderRadius: 10, border: 'none',
                background: orderItems.length > 0 ? 'linear-gradient(135deg,#3b82f6,#9333ea)' : '#f1f5f9',
                color: orderItems.length > 0 ? '#fff' : '#64748b',
                fontWeight: 800, fontSize: 13, cursor: 'pointer',
                boxShadow: orderItems.length > 0 ? '0 4px 14px rgba(59,130,246,0.3)' : 'none',
                transition: 'all 0.15s ease',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <ShoppingCart size={15} color={orderItems.length > 0 ? '#fff' : '#94a3b8'} />
                <span>Active Table Order</span>
                {orderItems.length > 0 && (
                  <span style={{
                    background: '#fff', color: '#3b82f6', borderRadius: 20,
                    padding: '2px 8px', fontSize: 10.5, fontWeight: 900,
                  }}>
                    {orderItems.reduce((s, i) => s + i.qty, 0)}
                  </span>
                )}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 14, fontWeight: 900, color: orderItems.length > 0 ? '#fff' : '#64748b' }}>
                  {sym}{total.toFixed(2)}
                </span>
                <span style={{ fontSize: 11, color: orderItems.length > 0 ? '#e0e7ff' : '#94a3b8' }}>View & Send ›</span>
              </div>
            </button>
          </div>

          {/* Search & Category Pills */}
          <div style={{ padding: '10px 16px 8px', borderBottom: '1px solid #f1f5f9' }}>
            <div style={{ position: 'relative', marginBottom: 8 }}>
              <Search size={13} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
              <input
                style={{
                  width: '100%', padding: '8px 12px 8px 30px', borderRadius: 8,
                  border: '1px solid #e2e8f0', background: '#f8fafc', fontSize: 12,
                  outline: 'none', boxSizing: 'border-box',
                }}
                placeholder="Search menu items..."
                value={productSearch}
                onChange={e => setProductSearch(e.target.value)}
              />
            </div>

      
          </div>

          {/* Product Catalog Grid (Bigger, Wider Cards) */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px', background: '#fafbfc', minHeight: 0 }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))', gap: 12 }}>
              {filteredProducts.map(p => {
                const cartQty = orderItems.filter(i => i.productId === p.id).reduce((s, i) => s + i.qty, 0);
                return (
                  <div
                    key={p.id}
                    onClick={() => addItemToOrder(p)}
                    style={{
                      position: 'relative', padding: '16px 18px', cursor: 'pointer',
                      borderRadius: 14, border: `2px solid ${cartQty > 0 ? '#3b82f6' : '#e2e8f0'}`,
                      background: cartQty > 0 ? 'linear-gradient(135deg,#eff6ff,#dbeafe)' : '#fff',
                      display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
                      boxShadow: cartQty > 0 ? '0 0 0 3px rgba(59,130,246,0.2)' : '0 2px 6px rgba(0,0,0,0.04)',
                      transition: 'all 0.12s ease', minHeight: 90,
                    }}
                  >
                    {cartQty > 0 && (
                      <div style={{
                        position: 'absolute', top: -8, right: -8, width: 24, height: 24, borderRadius: '50%',
                        background: '#3b82f6', color: '#fff', fontSize: 12, fontWeight: 900,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        boxShadow: '0 2px 6px rgba(59,130,246,0.4)', border: '2px solid #fff'
                      }}>{cartQty}</div>
                    )}
                    <div style={{ fontSize: 15, fontWeight: 800, color: '#0f172a', lineHeight: 1.35, marginBottom: 8 }}>
                      {p.name}
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: 16, fontWeight: 900, color: '#2563eb' }}>
                        {sym}{p.rate.toFixed(2)}
                      </span>
                      {p.category && (
                        <span style={{ fontSize: 11, color: '#64748b', fontWeight: 700, background: '#f1f5f9', padding: '3px 8px', borderRadius: 6 }}>
                          {p.category}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
              {filteredProducts.length === 0 && (
                <div style={{ gridColumn: '1 / -1', padding: '40px 10px', textAlign: 'center', color: '#94a3b8' }}>
                  <Search size={28} style={{ opacity: 0.25, marginBottom: 6 }} />
                  <div style={{ fontSize: 12.5, fontWeight: 700 }}>No matching items found</div>
                </div>
              )}
            </div>
          </div>

          {/* Active Items Drawer Overlay */}
          {showOrderPopup && (
            <div
              onClick={() => setShowOrderPopup(false)}
              style={{
                position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', backdropFilter: 'blur(2px)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20,
              }}
            >
              <div
                onClick={e => e.stopPropagation()}
                style={{
                  width: '92%', maxWidth: 460, maxHeight: '85vh', background: '#fff',
                  borderRadius: 16, display: 'flex', flexDirection: 'column', overflow: 'hidden',
                  boxShadow: '0 24px 60px rgba(15,23,42,0.3)',
                }}
              >
                <div style={{ padding: '16px 18px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div>
                    <div style={{ fontSize: 15, fontWeight: 800, color: '#0f172a' }}>Table {orderTable.number} Items</div>
                    <div style={{ fontSize: 11, color: '#64748b', fontWeight: 600, marginTop: 1 }}>
                      {orderItems.length} {orderItems.length === 1 ? 'item' : 'items'} · {sym}{total.toFixed(2)}
                    </div>
                  </div>
                  <button onClick={() => setShowOrderPopup(false)} style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer' }}>
                    <X size={18} />
                  </button>
                </div>

                <div style={{ flex: 1, overflowY: 'auto', padding: 12, display: 'flex', flexDirection: 'column', gap: 8, background: '#fafbfc' }}>
                  {orderItems.map(item => (
                    <div key={item.id} style={{
                      padding: '10px 12px', borderRadius: 10,
                      border: '1px solid #e2e8f0', borderLeft: `3px solid ${item.sentToKitchen ? '#10b981' : '#f59e0b'}`,
                      display: 'flex', alignItems: 'center', gap: 8, background: '#fff',
                    }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                          <span style={{ fontSize: 12.5, fontWeight: 800, color: '#0f172a' }}>{item.name}</span>
                          {/* Course Quick Selector */}
                          {!item.sentToKitchen && (
                            <div style={{ display: 'flex', gap: 3 }}>
                              {(['starter', 'main', 'beverage', 'dessert'] as CourseType[]).map(c => (
                                <button
                                  key={c}
                                  type="button"
                                  onClick={() => {
                                    setOrders(prev => ({
                                      ...prev,
                                      [orderTableId!]: (prev[orderTableId!] || []).map(i =>
                                        i.id === item.id ? { ...i, course: i.course === c ? undefined : c } : i
                                      ),
                                    }));
                                  }}
                                  title={`Tag as ${COURSE_META[c].label}`}
                                  style={{
                                    border: item.course === c ? `1.5px solid ${COURSE_META[c].color}` : '1px solid #e2e8f0',
                                    background: item.course === c ? `${COURSE_META[c].color}15` : '#fff',
                                    color: item.course === c ? COURSE_META[c].color : '#64748b',
                                    borderRadius: 4, padding: '1px 4px', fontSize: 9.5, fontWeight: 800, cursor: 'pointer',
                                  }}
                                >
                                  {COURSE_META[c].emoji}
                                </button>
                              ))}
                            </div>
                          )}
                          {item.sentToKitchen && item.course && (
                            <span style={{
                              fontSize: 9, fontWeight: 800, padding: '1px 5px', borderRadius: 4,
                              background: '#f8fafc', border: `1px solid ${COURSE_META[item.course].color}`,
                              color: COURSE_META[item.course].color,
                            }}>{COURSE_META[item.course].label}</span>
                          )}
                        </div>

                        {/* Kitchen Prep Notes Field */}
                        {!item.sentToKitchen ? (
                          <input
                            value={item.notes || ''}
                            onChange={e => {
                              const val = e.target.value;
                              setOrders(prev => ({
                                ...prev,
                                [orderTableId!]: (prev[orderTableId!] || []).map(i =>
                                  i.id === item.id ? { ...i, notes: val } : i
                                ),
                              }));
                            }}
                            placeholder="Kitchen notes: e.g. Less Spicy, No Garlic..."
                            style={{
                              width: '100%', padding: '3px 6px', borderRadius: 4, border: '1px dashed #cbd5e1',
                              fontSize: 10.5, fontStyle: 'italic', marginTop: 3, outline: 'none', background: '#fafbfc'
                            }}
                          />
                        ) : (
                          item.notes && (
                            <div style={{ fontSize: 10.5, fontStyle: 'italic', color: '#0284c7', marginTop: 1 }}>
                              👉 {item.notes}
                            </div>
                          )
                        )}
                        <div style={{ fontSize: 10, fontWeight: 700, color: item.sentToKitchen ? '#16a34a' : '#b45309', marginTop: 2 }}>
                          {item.sentToKitchen ? (item.kotRound ? `Sent · Round #${item.kotRound}` : 'Sent to kitchen') : 'Not sent'}
                        </div>
                      </div>

                      <div style={{ display: 'flex', alignItems: 'center', border: '1px solid #e2e8f0', borderRadius: 6, overflow: 'hidden', background: '#f8fafc' }}>
                        <button onClick={() => updateItemQty(item.id, -1)} style={{ padding: '4px 8px', border: 'none', background: 'transparent', cursor: 'pointer' }}><Minus size={10} /></button>
                        <span style={{ width: 22, textAlign: 'center', fontSize: 12, fontWeight: 800, color: '#0f172a' }}>{item.qty}</span>
                        <button onClick={() => updateItemQty(item.id, 1)} style={{ padding: '4px 8px', border: 'none', background: 'transparent', cursor: 'pointer' }}><Plus size={10} /></button>
                      </div>

                      <div style={{ fontSize: 12.5, fontWeight: 800, color: '#0f172a', minWidth: 50, textAlign: 'right' }}>
                        {sym}{(item.qty * item.rate).toFixed(2)}
                      </div>

                      <button onClick={() => removeItem(item.id)} style={{ background: 'none', border: 'none', color: '#cbd5e1', cursor: 'pointer' }}>
                        <X size={13} />
                      </button>
                    </div>
                  ))}
                </div>

                <div style={{ padding: 14, borderTop: '1px solid #f1f5f9', display: 'flex', flexDirection: 'column', gap: 8, background: '#fff' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                    <span style={{ fontSize: 12, fontWeight: 700, color: '#64748b' }}>TOTAL</span>
                    <span style={{ fontSize: 18, fontWeight: 900, color: '#0f172a' }}>{sym}{total.toFixed(2)}</span>
                  </div>

                  <div style={{ display: 'flex', gap: 8 }}>
                    <button
                      onClick={() => setKotPrintConfirm(true)}
                      disabled={unsentItems.length === 0}
                      style={{
                        flex: 1, padding: '10px 12px', borderRadius: 8, border: 'none',
                        background: unsentItems.length === 0 ? '#f1f5f9' : '#0284c7',
                        color: unsentItems.length === 0 ? '#94a3b8' : '#fff',
                        fontWeight: 800, fontSize: 12.5, cursor: unsentItems.length === 0 ? 'not-allowed' : 'pointer',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                      }}
                    >
                      <Send size={14} /> Send KOT {unsentItems.length > 0 ? `(${unsentItems.length})` : ''}
                    </button>

                    {!autoPrintKOT && (
                      <button
                        onClick={() => {
                          if (!lastKOT || lastKOT.tableId !== orderTableId) { alert('No KOT sent yet.'); return; }
                          printKOTTicket({ round: lastKOT.round, items: lastKOT.items, table: orderTable, ticketType: 'REPRINT' });
                        }}
                        disabled={!lastKOT || lastKOT.tableId !== orderTableId}
                        style={{
                          padding: '10px 14px', borderRadius: 8, border: '1px solid #e2e8f0',
                          background: '#f8fafc', color: '#334155', fontWeight: 800, fontSize: 12, cursor: 'pointer',
                        }}
                      >
                        🖨 Reprint
                      </button>
                    )}
                  </div>

                  <button
                    onClick={handleCheckout}
                    disabled={orderItems.length === 0}
                    style={{
                      width: '100%', padding: '13px 14px', borderRadius: 10, border: 'none',
                      background: orderItems.length === 0 ? '#e2e8f0' : 'linear-gradient(to right,#3b82f6,#9333ea)',
                      color: orderItems.length === 0 ? '#94a3b8' : '#fff',
                      fontWeight: 900, fontSize: 14, cursor: orderItems.length === 0 ? 'not-allowed' : 'pointer',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                      boxShadow: orderItems.length > 0 ? '0 4px 16px rgba(59,130,246,0.35)' : 'none',
                    }}
                  >
                    <Receipt size={15} /> Send to POS & Bill
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Confirmation Modals */}
      {kotPrintConfirm && (
        <div onClick={() => setKotPrintConfirm(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', zIndex: 1200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 14, padding: 20, maxWidth: 320, width: '100%', boxShadow: '0 20px 50px rgba(0,0,0,0.2)' }}>
            <div style={{ fontSize: 24, marginBottom: 4 }}>🖨️</div>
            <div style={{ fontSize: 15, fontWeight: 800, color: '#0f172a', marginBottom: 4 }}>Send KOT to Kitchen</div>
            <div style={{ fontSize: 12.5, color: '#64748b', marginBottom: 16 }}>Print a ticket or send directly to kitchen screen?</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <button onClick={() => { sendKOT(true); setKotPrintConfirm(false); }} style={{ padding: '9px 12px', borderRadius: 8, border: 'none', background: '#0284c7', color: '#fff', fontWeight: 800, fontSize: 12.5, cursor: 'pointer' }}>🖨️ Send & Print</button>
              <button onClick={() => { sendKOT(false); setKotPrintConfirm(false); }} style={{ padding: '9px 12px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff', color: '#334155', fontWeight: 800, fontSize: 12.5, cursor: 'pointer' }}>Send Without Printing</button>
              <button onClick={() => setKotPrintConfirm(false)} style={{ padding: '8px 12px', borderRadius: 8, border: 'none', background: 'transparent', color: '#94a3b8', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {vacantConfirm && (
        <div onClick={() => setVacantConfirm(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 14, padding: 22, maxWidth: 340, width: '100%', boxShadow: '0 20px 50px rgba(0,0,0,0.2)' }}>
            <div style={{ fontSize: 24, marginBottom: 4 }}>⚠️</div>
            <div style={{ fontSize: 15, fontWeight: 800, color: '#0f172a', marginBottom: 4 }}>Reset Table Vacant?</div>
            <div style={{ fontSize: 12.5, color: '#64748b', marginBottom: 16 }}>This will clear the current orders and mark the table as empty.</div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => setVacantConfirm(null)} style={{ flex: 1, background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: 8, padding: '9px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>Cancel</button>
              <button onClick={() => { markVacant(vacantConfirm.tableId); setVacantConfirm(null); }} style={{ flex: 1, background: '#dc2626', color: '#fff', border: 'none', borderRadius: 8, padding: '9px', fontSize: 12, fontWeight: 800, cursor: 'pointer' }}>Clear & Vacant</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Advanced Table Reservation Dialog (Add / Edit / Conflict Check) ── */}
      {reservationModal && (() => {
        const rTable = tables.find(t => t.id === reservationModal.tableId);
        const existingBookings = rTable ? getNormalizedTableReservations(rTable) : [];
        const isEdit = Boolean(reservationModal.resId);

        return (
          <div
            onClick={() => setReservationModal(null)}
            style={{
              position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.6)', backdropFilter: 'blur(2px)',
              zIndex: 1400, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
            }}
          >
            <div
              onClick={e => e.stopPropagation()}
              style={{
                background: '#fff', borderRadius: 18, maxWidth: 440, width: '100%',
                maxHeight: '92vh', overflowY: 'auto', padding: 24,
                boxShadow: '0 24px 60px rgba(15,23,42,0.25)', border: '1px solid #e2e8f0',
              }}
            >
              {/* Header */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 20 }}>📅</span>
                    <span style={{ fontSize: 17, fontWeight: 900, color: '#0f172a' }}>
                      {isEdit ? 'Edit Reservation' : 'New Table Reservation'}
                    </span>
                  </div>
                  <div style={{ fontSize: 12, color: '#64748b', fontWeight: 700, marginTop: 3 }}>
                    Table {rTable?.number} ({rTable?.seats} Seats) · {floors.find(f => f.id === rTable?.floorId)?.name}
                  </div>
                </div>
                <button
                  onClick={() => setReservationModal(null)}
                  style={{ border: 'none', background: 'transparent', color: '#94a3b8', cursor: 'pointer', padding: 4 }}
                ><X size={18} /></button>
              </div>

              {/* Conflict Alert Banner */}
              {conflictWarning && (
                <div style={{
                  padding: '10px 12px', background: '#fef2f2', border: '1.5px solid #f87171',
                  borderRadius: 10, color: '#991b1b', fontSize: 12, fontWeight: 700,
                  marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8,
                }}>
                  <AlertTriangle size={16} style={{ flexShrink: 0 }} />
                  <span>{conflictWarning}</span>
                </div>
              )}

              {/* Input Fields */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 20 }}>
                <div>
                  <label style={{ fontSize: 11, fontWeight: 800, color: '#475569', textTransform: 'uppercase', display: 'block', marginBottom: 5 }}>
                    Guest / Party Name *
                  </label>
                  <input
                    autoFocus
                    value={resName}
                    onChange={e => setResName(e.target.value)}
                    placeholder="e.g. Robert Smith (VIP / Family)"
                    style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: '1.5px solid #cbd5e1', fontSize: 13, fontWeight: 700, boxSizing: 'border-box' }}
                  />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 0.8fr', gap: 10 }}>
                  <div>
                    <label style={{ fontSize: 11, fontWeight: 800, color: '#475569', textTransform: 'uppercase', display: 'block', marginBottom: 5 }}>
                      Phone / Mobile
                    </label>
                    <input
                      value={resPhone}
                      onChange={e => setResPhone(e.target.value)}
                      placeholder="e.g. +1 555-0199"
                      style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: '1.5px solid #cbd5e1', fontSize: 13, fontWeight: 600, boxSizing: 'border-box' }}
                    />
                  </div>
                  <div>
                    <label style={{ fontSize: 11, fontWeight: 800, color: '#475569', textTransform: 'uppercase', display: 'block', marginBottom: 5 }}>
                      Covers (PAX)
                    </label>
                    <input
                      type="number" min={1} max={50}
                      value={resParty}
                      onChange={e => setResParty(Math.max(1, parseInt(e.target.value) || 1))}
                      style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: '1.5px solid #cbd5e1', fontSize: 13, fontWeight: 800, boxSizing: 'border-box' }}
                    />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <div>
                    <label style={{ fontSize: 11, fontWeight: 800, color: '#475569', textTransform: 'uppercase', display: 'block', marginBottom: 5 }}>
                      Booking Date *
                    </label>
                    <input
                      type="date"
                      value={resDate}
                      min={todayLocalISODate()}
                      onChange={e => setResDate(e.target.value)}
                      style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1.5px solid #cbd5e1', fontSize: 12.5, fontWeight: 700, boxSizing: 'border-box' }}
                    />
                  </div>
                  <div>
                    <label style={{ fontSize: 11, fontWeight: 800, color: '#475569', textTransform: 'uppercase', display: 'block', marginBottom: 5 }}>
                      Arrival Time *
                    </label>
                    <input
                      type="time"
                      value={resTime}
                      onChange={e => setResTime(e.target.value)}
                      style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1.5px solid #cbd5e1', fontSize: 12.5, fontWeight: 800, boxSizing: 'border-box' }}
                    />
                  </div>
                </div>

                {/* Duration Buttons */}
                <div>
                  <label style={{ fontSize: 11, fontWeight: 800, color: '#475569', textTransform: 'uppercase', display: 'block', marginBottom: 5 }}>
                    Table Slot Duration
                  </label>
                  <div style={{ display: 'flex', gap: 6 }}>
                    {[45, 60, 90, 120, 180].map(mins => (
                      <button
                        key={mins}
                        type="button"
                        onClick={() => setResDuration(mins)}
                        style={{
                          flex: 1, padding: '7px 4px', borderRadius: 8,
                          border: resDuration === mins ? '2px solid #6366f1' : '1px solid #e2e8f0',
                          background: resDuration === mins ? '#eef2ff' : '#fff',
                          color: resDuration === mins ? '#4338ca' : '#475569',
                          fontSize: 11.5, fontWeight: 800, cursor: 'pointer',
                        }}
                      >
                        {mins}m
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label style={{ fontSize: 11, fontWeight: 800, color: '#475569', textTransform: 'uppercase', display: 'block', marginBottom: 5 }}>
                    Special Requests / Notes
                  </label>
                  <input
                    value={resNotes}
                    onChange={e => setResNotes(e.target.value)}
                    placeholder="e.g. Window booth, Birthday cake, High chair..."
                    style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: '1.5px solid #cbd5e1', fontSize: 12.5, boxSizing: 'border-box' }}
                  />
                </div>
              </div>

              {/* Other bookings on this table for this date */}
              {existingBookings.filter(b => b.date === resDate && b.id !== reservationModal.resId).length > 0 && (
                <div style={{ marginBottom: 18, background: '#f8fafc', padding: '10px 12px', borderRadius: 10, border: '1px solid #e2e8f0' }}>
                  <div style={{ fontSize: 11, fontWeight: 800, color: '#64748b', marginBottom: 6 }}>
                    Other Bookings for Table {rTable?.number} on {resDate}:
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {existingBookings.filter(b => b.date === resDate && b.id !== reservationModal.resId).map(b => (
                      <div key={b.id} style={{ fontSize: 11.5, color: '#334155', fontWeight: 700, display: 'flex', justifyContent: 'space-between' }}>
                        <span>🕒 {b.time} ({b.durationMinutes}m) · {b.name}</span>
                        <span style={{ color: '#6366f1' }}>{b.partySize} PAX</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  onClick={() => setReservationModal(null)}
                  style={{ flex: 1, background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: 10, padding: '11px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}
                >
                  Cancel
                </button>
                {isEdit && (
                  <button
                    onClick={() => {
                      if (window.confirm('Cancel this reservation?')) {
                        updateReservationStatus(reservationModal.tableId, reservationModal.resId!, 'cancelled');
                        setReservationModal(null);
                      }
                    }}
                    style={{ background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca', borderRadius: 10, padding: '11px 14px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}
                  >
                    Cancel Booking
                  </button>
                )}
                <button
                  onClick={saveReservation}
                  style={{
                    flex: 1.5, background: 'linear-gradient(135deg,#4f46e5,#6366f1)', color: '#fff',
                    border: 'none', borderRadius: 10, padding: '11px', fontSize: 13, fontWeight: 900,
                    cursor: 'pointer', boxShadow: '0 4px 14px rgba(79,70,229,0.3)',
                  }}
                >
                  {isEdit ? 'Save Changes' : 'Confirm Reservation'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Master Reservation Book Modal (Outlet-Wide Bookings) ── */}
      {showAllReservationsModal && (() => {
        const allReservations: (TableReservation & { tableNumber: string; floorName: string })[] = [];
        tables.forEach(t => {
          const fl = floors.find(f => f.id === t.floorId)?.name || 'Main';
          getNormalizedTableReservations(t).forEach(r => {
            allReservations.push({ ...r, tableNumber: t.number, floorName: fl });
          });
        });

        const filtered = allReservations
          .filter(r => r.date === resViewDate)
          .filter(r => resStatusFilter === 'all' || r.status === resStatusFilter)
          .sort((a, b) => timeToMins(a.time) - timeToMins(b.time));

        return (
          <div
            onClick={() => setShowAllReservationsModal(false)}
            style={{
              position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.6)', backdropFilter: 'blur(2px)',
              zIndex: 1350, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
            }}
          >
            <div
              onClick={e => e.stopPropagation()}
              style={{
                width: '95%', maxWidth: 760, maxHeight: '88vh', background: '#fff',
                borderRadius: 20, display: 'flex', flexDirection: 'column', overflow: 'hidden',
                boxShadow: '0 25px 70px rgba(15,23,42,0.3)', border: '1px solid #e2e8f0',
              }}
            >
              {/* Header */}
              <div style={{ padding: '18px 22px', borderBottom: '1px solid #f1f5f9', background: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <div style={{ fontSize: 17, fontWeight: 900, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span>📖</span> Reservation Master Book
                  </div>
                  <div style={{ fontSize: 12, color: '#64748b', fontWeight: 600, marginTop: 2 }}>
                    Manage bookings, future dates, and seat incoming parties
                  </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <input
                    type="date"
                    value={resViewDate}
                    onChange={e => setResViewDate(e.target.value)}
                    style={{ padding: '6px 10px', borderRadius: 8, border: '1.5px solid #cbd5e1', fontSize: 12, fontWeight: 800 }}
                  />
                  <button
                    onClick={() => setShowAllReservationsModal(false)}
                    style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: '#94a3b8' }}
                  ><X size={20} /></button>
                </div>
              </div>

              {/* Filter Tabs */}
              <div style={{ padding: '10px 22px', borderBottom: '1px solid #f1f5f9', display: 'flex', gap: 8, background: '#fff' }}>
                {(['all', 'confirmed', 'seated'] as const).map(tab => (
                  <button
                    key={tab}
                    onClick={() => setResStatusFilter(tab)}
                    style={{
                      padding: '5px 12px', borderRadius: 8, border: 'none', cursor: 'pointer',
                      background: resStatusFilter === tab ? '#6366f1' : '#f1f5f9',
                      color: resStatusFilter === tab ? '#fff' : '#64748b',
                      fontSize: 11.5, fontWeight: 800, textTransform: 'capitalize',
                    }}
                  >
                    {tab === 'all' ? `All Bookings (${allReservations.filter(r => r.date === resViewDate).length})` : tab}
                  </button>
                ))}
              </div>

              {/* Booking List */}
              <div style={{ flex: 1, overflowY: 'auto', padding: 16, display: 'flex', flexDirection: 'column', gap: 10, background: '#fafbfc' }}>
                {filtered.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '50px 20px', color: '#94a3b8' }}>
                    <Calendar size={36} style={{ opacity: 0.3, marginBottom: 8 }} />
                    <div style={{ fontSize: 14, fontWeight: 800, color: '#334155' }}>No reservations for {resViewDate}</div>
                    <div style={{ fontSize: 12, marginTop: 4 }}>Select another date above or right-click any table to book a guest.</div>
                  </div>
                ) : (
                  filtered.map(r => (
                    <div
                      key={r.id}
                      style={{
                        background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0',
                        borderLeft: `4px solid ${r.status === 'seated' ? '#10b981' : '#6366f1'}`,
                        padding: '12px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        boxShadow: '0 1px 3px rgba(15,23,42,0.04)',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                        <div style={{
                          background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10,
                          padding: '6px 12px', textAlign: 'center', minWidth: 62,
                        }}>
                          <div style={{ fontSize: 14, fontWeight: 900, color: '#0f172a' }}>{r.time}</div>
                          <div style={{ fontSize: 10, fontWeight: 700, color: '#64748b' }}>{r.durationMinutes}m</div>
                        </div>

                        <div>
                          <div style={{ fontSize: 14, fontWeight: 900, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 6 }}>
                            <span>{r.name}</span>
                            <span style={{ fontSize: 11, fontWeight: 700, color: '#64748b' }}>({r.partySize} Guests)</span>
                            {r.status === 'seated' && (
                              <span style={{ fontSize: 9.5, fontWeight: 800, background: '#dcfce7', color: '#15803d', padding: '1px 6px', borderRadius: 6 }}>
                                Currently Seated
                              </span>
                            )}
                          </div>
                          <div style={{ fontSize: 11.5, color: '#64748b', marginTop: 2 }}>
                            <span>Table <b>{r.tableNumber}</b> ({r.floorName})</span>
                            {r.phone && <span style={{ marginLeft: 8 }}>· 📞 {r.phone}</span>}
                          </div>
                          {r.specialRequests && (
                            <div style={{ fontSize: 11, fontStyle: 'italic', color: '#6366f1', marginTop: 3 }}>
                              👉 Note: {r.specialRequests}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Seat or Edit */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        {r.status !== 'seated' && (
                          <button
                            onClick={() => seatReservation(r.tableId, r)}
                            style={{
                              background: '#10b981', color: '#fff', border: 'none', borderRadius: 8,
                              padding: '7px 14px', fontSize: 12, fontWeight: 800, cursor: 'pointer',
                              display: 'flex', alignItems: 'center', gap: 5,
                            }}
                          >
                            <UserCheck size={13} /> Seat Party
                          </button>
                        )}
                        <button
                          onClick={() => {
                            setShowAllReservationsModal(false);
                            openReserveModal(r.tableId, r.id);
                          }}
                          style={{
                            background: '#f8fafc', color: '#334155', border: '1px solid #e2e8f0',
                            borderRadius: 8, padding: '7px 12px', fontSize: 12, fontWeight: 700, cursor: 'pointer',
                          }}
                        >
                          Edit
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {showHistoryModal && (
        <div onClick={() => setShowHistoryModal(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', zIndex: 1300, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 16, maxWidth: 500, width: '100%', maxHeight: '80vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <div style={{ padding: '16px 18px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ fontSize: 15, fontWeight: 800, color: '#0f172a' }}>KOT Running History</div>
              <button onClick={() => setShowHistoryModal(false)} style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: '#94a3b8' }}><X size={18} /></button>
            </div>
            <div style={{ flex: 1, overflowY: 'auto', padding: 14, display: 'flex', flexDirection: 'column', gap: 8, background: '#fafbfc' }}>
              {kotHistory.length === 0 ? (
                <div style={{ textAlign: 'center', color: '#94a3b8', padding: '40px 10px' }}>No tickets yet</div>
              ) : (
                kotHistory.map(entry => (
                  <div key={entry.id} style={{ background: '#fff', borderRadius: 10, border: '1px solid #e2e8f0', padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, fontWeight: 800, color: '#0f172a' }}>
                      <span>KOT #{entry.kotNumber} (T-{entry.tableNumber})</span>
                      <span style={{ fontSize: 11, color: '#64748b' }}>{new Date(entry.timestamp).toLocaleTimeString()}</span>
                    </div>
                    <div style={{ fontSize: 11.5, color: '#475569' }}>
                      {entry.items.map((it, idx) => <span key={idx} style={{ marginRight: 8 }}>{it.qty}x {it.name}</span>)}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
